// Boot og hovedløkke: Pixi-kortet, DOM-UI'et, input, lyd og en fast 10 Hz simulation
// med akkumulator og interpolation. Sim-hændelser driver effekter, lyd, skærme og skærmlæser.

import { TeamLog } from './ui/teamlog';
import { BitmapFont } from 'pixi.js';
import './ui/fun.css';
import { autoplayBrain } from './ai/autoplay';
import { Pilot } from './ai/pilot';
import { newbieBrain } from './ai/balance';
import type { AutoplayModel } from './ai/autoplay';
import { adviceDrag, adviceIntent, adviceText, blockedCity, blockedText, cityAdvice, guestName, hz } from './ai/coach';
import { roleOf } from './sim/roles';
import type { Advice } from './ai/coach';
import { bottleneck, bottleneckText, hintFor } from './ai/truth';
import type { Shown } from './ai/truth';

/** Så længe er hånd og hint spillerens, før Volt må tage trækket (ms, mens uret går). */
const RESERVE_MS = 8000;
import { Audio } from './audio/director';
import { atlasFact, atlasId } from './content/atlas-facts';
import { PACKS } from './content/packs';
import { FINALE_LEVEL, LEVELS, getLevel } from './content/levels';
import { install, readParams } from './debug';
import { Game } from './game';
import { codeFromUrl } from './net/protocol';
import { Online } from './net/online';
import { Controls } from './input/controls';
import { Atlas } from './render/atlas';
import { createApp, maxResolution } from './render/app';
import { NUM_FONT } from './render/fx';
import { C } from './render/palette';
import { Quality } from './render/quality';
import { Scene } from './render/scene';
import { cloneState, darkInTicks, difficulty, hash, summary, TICK_HZ, trustMax } from './sim';
import type { CardOption, DifficultyId, GridNode, PackId, SimEvent, State, ThreatKind, YearSummary } from './sim';
import { exposedLines, REINFORCE_ALL } from './sim/actions';
import { calendarYear, retryMercy } from './sim/year';
import { earlyBonusPct, REFILLABLE } from './sim/economy';
import { isPowered } from './sim/cities';
import { nodeById, REINFORCE_COST, slotsFree } from './sim/grid';
import { markDirty } from './sim/flow';
import { powerKind } from './sim/powers';
import { CALM_CHZ, IMPLEMENTED, isCard, RIDE_BONUS, startThreat } from './sim/threats';
import { atlasUnlocked, dailyStreak, lessonDone, load, markLesson, recordDaily, recordStars, resetLessons, save, unlockAtlas, volumes } from './storage';
import { dailyPlan, streakNow } from './content/daily';
import type { DailyPlan } from './content/daily';
import { campaignDone } from './ui/screens/campaign';
import { dailyBrief, dailyShare } from './ui/screens/daily';
import { dailyPar } from './ai/par';
import type { Lesson, MotionMode, QualityMode } from './storage';
import { announce, clearLive, initA11y } from './ui/a11y';
import { BottomBar } from './ui/bottombar';
import { CARD_OPTION, CardPanel } from './ui/cards';
import { DuoPanel } from './ui/duo';
import { h } from './ui/dom';
import { Guide } from './ui/guide';
import type { P } from './render/geo';
import type { GuideShow } from './ui/guide';
import { PilotBar } from './ui/pilot-bar';
import { TipsTour } from './ui/tips';
import { Hud } from './ui/hud';
import { POWER_TEXT, PowerBar } from './ui/powerups';
import { hzText } from './sim/hz';
import { ICON } from './ui/icons';
import { RingMenu } from './ui/ring';
import type { RingArea } from './ui/ring';
import { atlasScreen, atlasTotal } from './ui/screens/atlas';
import { homeScreen } from './ui/screens/home';
import { levelStartScreen } from './ui/screens/levelstart';
import type { TeamPick } from './ui/screens/levelstart';
import { resultScreen } from './ui/screens/result';
import { Screens } from './ui/screens/screens';
import type { ScreenId } from './ui/screens/screens';
import { settingsScreen } from './ui/screens/settings';
import { pauseScreen } from './ui/screens/pause';
import { titleScreen } from './ui/screens/title';
import { yearEndScreen } from './ui/screens/yearend';
import type { YearEndView } from './ui/screens/yearend';
import { num, THREAT_NAMES } from './ui/text';
import { Forecast } from './ui/forecast';
import { COIN_MS, Juice, waitingCustomers } from './ui/juice';
import { arbitrate, toastChannel } from './ui/messages';
import { FocusStack } from './ui/focus';
import type { Channel } from './ui/messages';
import { Toast } from './ui/toast';
import { NodeCard } from './ui/nodecard';
import { VoltSession } from './ui/volt-session';
import type { SoloRole } from './ui/volt-session';
import { BRAND } from './brand';
import { applyFixture, applyFixtureLevels, fixtureFrom, fixtureTransport } from './dev/fixtures';
import { mountFixtureToggle } from './dev/toggle';

const params = readParams(location.search);
/** Værste-tilfælde-data (break-ui), kun i dev: `?data=worst|empty|one|huge`. Rystes ud af bygget. */
const fixture = import.meta.env.DEV ? fixtureFrom(location.search) : null;
if (import.meta.env.DEV && fixture) applyFixtureLevels(LEVELS, fixture);

/** Bane 1's øvelsesstorm er planlagt eller i gang (den hører ikke til nogen front). */
function tutorialStorm(s: State): boolean {
  if (s.director.planned.some((p) => p.tutorial)) return true;
  return s.threats.some((t) => t.kind === 'storm' && !t.front && t.phase !== 'done');
}
const saved = load();
const touch = window.matchMedia('(pointer: coarse)').matches;
const mobile = touch || Math.min(window.innerWidth, window.innerHeight) < 600;
/** Systemet beder om mindre bevægelse. Indstillingen Bevægelse kan overstyre det. */
const systemCalm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
let motion: MotionMode = saved.motion ?? 'auto';
const calmNow = (): boolean => motion === 'calm' || (motion === 'auto' && systemCalm);
/** Ryst: Fuld slår det til overalt, Rolig fra, Auto kun på store skærme (telefoner ryster i hånden). */
const shakeNow = (): boolean => (motion === 'full' ? true : calmNow() ? false : !mobile);

async function loadFonts(): Promise<void> {
  if (!document.fonts) return;
  const want = ['300 16px Inter', '400 16px Inter', '600 16px Inter', '800 16px Inter'];
  const all = Promise.all(want.map((f) => document.fonts.load(f))).then(() => undefined);
  // Fonten må aldrig holde spillet tilbage: efter 2,5 s tegner vi med reservefonten.
  await Promise.race([all, new Promise<void>((r) => setTimeout(r, 2500))]).catch(() => undefined);
}

function installNumberFont(): void {
  BitmapFont.install({
    name: NUM_FONT,
    style: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '800',
      fontSize: 44,
      fill: BRAND.white,
      stroke: { color: C.ink, width: 7, join: 'round' },
    },
    chars: [['0', '9'], ' kr+-s!%,.×Hz'],
    resolution: 2,
    padding: 6,
  });
}

async function boot(): Promise<void> {
  const rootEl = document.getElementById('app');
  const stageEl = document.getElementById('stage');
  if (!rootEl || !stageEl) throw new Error('Mangler #app eller #stage');
  const root: HTMLElement = rootEl;
  const stage: HTMLElement = stageEl;
  initA11y(root);
  await loadFonts();
  installNumberFont();

  let diff: DifficultyId = new URLSearchParams(location.search).has('difficulty') ? params.difficulty : saved.difficulty;
  let muted = params.mute || saved.muted;
  let autoplay: AutoplayModel | null = params.autoplay;
  let fps = 60;

  const quality = new Quality(params.quality, maxResolution(), mobile);
  let qualityMode: QualityMode = (params.quality as QualityMode | null) ?? saved.quality ?? 'auto';
  if (!params.quality && saved.quality) quality.setMode(saved.quality);
  let scene: Scene | null = null;
  const app = await createApp(stage, {
    renderer: params.renderer,
    resolution: quality.settings.resolution,
    onContextRestored: () => scene?.restored(),
  });
  const atlas = new Atlas(app.renderer);
  const audio = new Audio(muted);
  audio.haptics = saved.haptics ?? true;
  const sfx = audio.sfx;
  /** Titlens levende baggrund: en ekspert-bot spiller banen bag titlen. */
  let attract = false;

  const sc = new Scene(app, atlas, quality, {
    mobile,
    calm: calmNow(),
    onCityLit: (id) => {
      if (attract) return;
      audio.cityLit();
      const n = nodeById(game.state, id);
      if (n && game.running) announce(`${n.name} har strøm.`);
    },
    // Makkerrum: kameraet flytter sig aldrig uden et ord, så ingen af jer mister det, I kiggede på.
    onReframe: () => {
      if (online.inGame && !attract && game.running) toast.show('Kortet flytter sig, så I kan se alle byerne.', 'info', 2200);
    },
  });
  scene = sc;
  sc.setMotion(calmNow(), shakeNow());
  document.documentElement.classList.toggle('calm', calmNow());
  // CSS kender trinnet (html[data-q]): på lav kvalitet dropper skærmene den dyre sløring.
  document.documentElement.dataset.q = quality.name;
  quality.onChange((q) => {
    app.renderer.resize(app.screen.width, app.screen.height, q.resolution);
    sc.qualityChanged();
    document.documentElement.dataset.q = quality.name;
  });

  // -------------------------------------------------------------------------
  // UI

  const hud = new Hud({
    pause: () => togglePause(),
    speed: () => toggleSpeed(),
    mute: () => {
      muted = !muted;
      audio.setMuted(muted);
      save({ muted });
    },
    menu: () => (online.inGame ? online.menu() : showPause()),
    callYear: () => callYear(),
    ai: () => (pilot ? stopPilot() : startPilot()),
  });
  const pilotBar = new PilotBar(() => stopPilot());
  // Tips-turen (src/ui/tips.ts): én gang efter første ledning, og igen fra pausearket.
  const tips = new TipsTour((skipped) => {
    save({ tips: true });
    if (screens.current === null) game.held = false;
    stage.focus({ preventScroll: true });
    announce(skipped ? 'Tips sprunget over. De ligger i pausemenuen.' : 'Spillet kører igen.');
  });
  /** Hvornår turen skal komme (lidt efter første ledning, så jublen når at lande). */
  let tipsDue = 0;
  const bar = new BottomBar({
    tool: (t) => controls.setTool(t),
    hire: () => controls.act('hire_crew'),
    storm: () => controls.act('reinforce_mast', REINFORCE_ALL),
    undo: () => {
      if (!game.undo()) toast.say('Fortryd virker kun på din nyeste ledning, de første 3 sekunder.', 'info', 2600);
      else sfx.play('tap');
    },
  });
  const ring = new RingMenu();
  const toast = new Toast();
  // Vejrudsigten: de næste fronter med nedtælling, i en strimmel under HUD'en.
  const fc = new Forecast();
  const juice = new Juice();
  // Hændelseskort og power-ups (M2). Svarene går som intents ad samme vej som alt andet.
  const cards = new CardPanel({
    answer: (id, opt) => {
      const r = game.push('answer_card', id, opt);
      if (r.ok) {
        sfx.play('tap');
        lessonAct('answer_card');
      } else if (r.error === 'paused') controls.fail('answer_card', r.error);
    },
    // Fanen bag en menu: luk menuen, så kortet står fremme igen (IGN-løkke 2, runde 3).
    front: () => {
      ring.close();
      nodeCard.hide();
      controls.select(null);
      online.coop.closeWheel();
      sfx.play('tap');
    },
  });
  // Det fælles lyn (spor "coop"): to opgaver, én pr. rolle. Knappen sender den rigtige handling.
  const duo = new DuoPanel({
    act: (kind, line) => {
      if (game.push(kind, line).ok) sfx.play('tap');
      else sfx.play('error');
    },
  });
  /** Tick, hvor Driftsvagten sidst fik at vide, at frekvensen var langt ude. */
  let hzToldAt = -Infinity;
  const powers = new PowerBar({
    use: (id, target) => {
      // Lyden kommer fra lyddirektøren, når power_used lander.
      if (game.push(powerKind(id), target).ok) lessonAct(powerKind(id));
      else sfx.play('error');
    },
    trim: () => {
      if (game.push('trim_hz').ok) sfx.play('tap');
      else sfx.play('error');
    },
  });
  const screens = new Screens();
  const bubble = h('div', { class: 'bubble', hidden: true, 'aria-hidden': 'true' });
  const nodeCard = new NodeCard();
  /** Ét fokuslag ad gangen: ring, hændelseskort, bykort, pingehjul eller gavevalg (Spor: kortet). */
  const focus = new FocusStack();
  const guide = new Guide();
  // Kun synlig, når man har zoomet eller panoreret væk. Etiketten gør den til en tydelig vej hjem.
  const recenterBtn = h(
    'button',
    { type: 'button', class: 'recenter', hidden: true, title: 'Vis hele kortet', onclick: () => sc.recenter() },
    h('span', { html: ICON.target, 'aria-hidden': 'true' }),
    h('span', {}, 'Hele kortet'),
  ) as HTMLButtonElement;
  // Spor: telefonkort: en by med løbende ur uden for billedet. Kameraet glider ikke længere selv lige
  // efter spillerens tryk og træk; chippen peger mod byen, og et tryk glider derhen.
  const findArrow = h('span', { class: 'find-arrow', html: ICON.next, 'aria-hidden': 'true' });
  const findBtn = h(
    'button',
    { type: 'button', class: 'recenter find-clock', hidden: true, title: 'Vis byen med uret', onclick: () => sc.findClock() },
    findArrow,
    h('span', { html: ICON.clock, 'aria-hidden': 'true' }),
    h('span', {}, 'Find byen'),
  ) as HTMLButtonElement;
  root.append(juice.el, hud.el, fc.el, fc.live, toast.hint, pilotBar.el, tips.el, bar.el, powers.el, cards.el, duo.el, toast.el, guide.el, guide.cheerEl, bubble, nodeCard.el, recenterBtn, findBtn, ring.el, screens.el);
  // IGN-løkke 2: en toast fra spillet ("Holstebro har ikke flere pladser") stod tilbage på årsskiftet,
  // resultatet og i lobbyen. Et skærmskift rydder beskederne; nye beskeder på skærmen vises som normalt.
  screens.onChange = () => {
    toast.clear();
    ring.close();
  };

  // -------------------------------------------------------------------------
  // Spillet

  let levelId = getLevel(params.level).id;
  let yearView: YearEndView | null = null;
  let yearShown = -1;
  /** Årsskiftet kom midt i et træk: skærmen vises, når fingeren slipper. */
  let pendingYear: { s: State; year: number; stats: YearSummary } | null = null;
  /** Solo: årsskiftets skærm holder spillet, til en gave er valgt. */
  let yearHeld = false;
  let resultTimer = 0;
  let homeResume: (() => void) | null = null;
  /** Bane i gang bag titlen (Fortsæt), husket mens man er i banevalg, indstillinger og atlas. */
  let pendingResume: { title: string; go(): void } | undefined;
  /** Skærmen, Tilbage fra banestart, indstillinger og atlas fører til. */
  let backTo: ScreenId = 'title';
  let attractTimer = 0;

  const game = new Game(levelId, diff, params.seed, {
    beforeTick: (s) => sc.snapshot(s),
    events: (evs, s) => onEvents(evs, s),
    rejected: (kind, error) => {
      if (!game.brain) controls.fail(kind, error);
    },
  });
  game.speed = saved.speed === 2 ? 2 : 1;
  // ?spol=0 slår auto-spol fra (til målinger af, hvad det sparer).
  game.spol.disabled = new URLSearchParams(location.search).get('spol') === '0';
  let spolTold = false;
  // Ethvert tryk, klik, hjul eller tastetryk tager farten af med det samme: spilleren vil gøre noget.
  for (const ev of ['pointerdown', 'keydown', 'wheel'] as const) window.addEventListener(ev, () => game.spol.drop(), { capture: true, passive: true });

  // Volt, AI-makkeren (M3): p2 i co-op, med panel, markør og logbog. ?p2=volt|bot slår ham til.
  const volt = new VoltSession({
    game,
    scene: sc,
    atlas,
    cards,
    toast,
    stage,
    root,
    agent: params.agent,
    wrap: import.meta.env.DEV && fixture ? (t) => fixtureTransport(t, fixture) : undefined,
    covered: () => screens.current !== null,
    live: () => !attract && started,
    // Første bane lærer at trække en ledning og at stå en storm: Volt tier og venter, til spilleren
    // har trukket sin første ledning, og øvelsesstormen er drevet over. Intet loft i sekunder.
    // Er spilleren driftsvagt, kan spilleren slet ikke trække ledninger: så venter Volt ikke på det.
    hold: () =>
      !lessonDone('drag') && !(game.rolesLive && game.player === 'p2')
        ? 'Din tur først: træk en ledning, så går jeg i gang.'
        : tutorialStorm(game.state)
          ? 'Øvelsesstormen er din. Jeg kigger med.'
          : null,
    // Det, hånden og hintet peger på, er spillerens; ellers tager Volt det lige foran dem.
    // IGN runde 4: står rådet ubesvaret i 8 sekunder, mens uret går, tager Volt det, så byen ikke går i sort.
    reserved: () => {
      const a = advice;
      // Er rådet Volts job, er det ikke spillerens at holde fri.
      if (!a || a.partner) return [];
      if (!game.paused && performance.now() - adviceSince > RESERVE_MS) return [];
      if (a.kind === 'build') return [a.node, a.to];
      if ('line' in a && a.line) return [a.line];
      if (a.kind === 'shoo') return [a.threat];
      if (a.kind === 'station' || a.kind === 'battery' || a.kind === 'flywheel') return [a.node];
      return [];
    },
  });
  if (params.p2) volt.set(params.p2);
  /** Solo med Volt som makker i roller (ikke et rum, ikke titlens baggrund). */
  const soloVolt = (): boolean => volt.on && game.rolesLive && !online.inGame && !game.remote && !attract && !game.over;

  const controls = new Controls({
    game,
    scene: sc,
    ring,
    toast,
    bar,
    sfx,
    stage,
    bubble,
    active: () => screens.current === null && !game.over,
    togglePause: () => togglePause(),
    toggleSpeed: () => toggleSpeed(),
    callYear: () => callYear(),
    adviceLine: () => (advice && 'line' in advice && advice.line ? advice.line : null),
    inspect: (id) => nodeCard.show(id),
    // IGN-løkke 2, runde 3: Volt kan også spørges i solo ("Forstærk? Spørg Volt"), og han svarer selv.
    ask: (at, kind) => (online.live ? online.ask(at, kind) : soloVolt() ? volt.ask(at, kind) : false),
    canAsk: () => online.live || soloVolt(),
    partnerName: () => (soloVolt() ? 'Volt' : null),
    ringArea: () => ringArea(),
    card: (id) => cards.focus(id),
    powers,
    cardPanel: cards,
    onAct: (kind) => {
      // Spor: kortet: kameraet står stille lige efter et byg, så det byggede ikke glider væk.
      sc.holdForAction();
      lessonAct(kind);
    },
    vibrate: (p) => audio.vibrate(p),
    escape: () => {
      const cur = screens.current;
      if (cur === 'room') return online.escape();
      if (cur === 'pause') {
        resumeFromPause();
        return true;
      }
      if (cur === 'settings' || cur === 'atlas' || cur === 'levelstart') {
        goBack();
        return true;
      }
      if (cur === 'result' || cur === 'home') {
        showTitle();
        return true;
      }
      if (cur === 'title' && homeResume) {
        homeResume();
        return true;
      }
      return cur !== null;
    },
  });

  // Makkerspil (M4): rummet ejer banen, og denne skærm afspiller den. Se src/net/online.ts.
  const online = new Online({
    game,
    scene: sc,
    stage,
    root,
    screens,
    toast,
    sfx,
    vibrate: (p) => audio.vibrate(p),
    enter: () => enterOnline(),
    resynced: () => sc.snapshot(game.state),
    leftGame: () => {
      window.clearTimeout(resultTimer);
      started = false;
      yearView = null;
      startAttract();
    },
    fail: (kind, error) => controls.fail(kind, error),
    title: () => {
      started = false;
      showTitle();
    },
  });

  online.coop.dockBadge(hud.el.querySelector('.controls'));

  /** Hvem gjorde hvad i makkerspillet, til resultatskærmen (IGN runde 4). */
  const teamLog = new TeamLog();

  function enterOnline(): void {
    window.clearTimeout(resultTimer);
    window.clearTimeout(attractTimer);
    homeResume = null;
    pendingResume = undefined;
    pilot = null;
    game.brain = null;
    // Sammen med en ven er p2 makkeren, ikke Volt: rummet ejer banen, så Volt må ikke skubbe træk ind.
    volt.set(null);
    levelId = game.state.levelId;
    yearView = null;
    teamLog.reset();
    resetHints();
    ring.close();
    controls.select(null);
    bar.pick('build');
    sc.setLevel(game.state, game.level);
    fc.reset();
    juice.reset();
    layout();
    const paused = game.paused;
    play();
    game.paused = paused;
    if (game.over) showResult();
    else {
      // Rollefordeling med det samme: hvem er hvem (samme mærker som på ledningerne og i mærket øverst).
      const look = (s: 'p1' | 'p2'): string => (s === 'p1' ? 'grøn og rund' : 'petrol og firkantet');
      const me = game.player === 'p2' ? 'p2' : 'p1';
      const say = touch ? 'hold fingeren på kortet for at sige noget' : 'højreklik eller tryk G for at sige noget';
      // IGN runde 4: rollerne er regler, så velkomsten siger, hvad du må, og hvad makker gør.
      const job =
        me === 'p1'
          ? 'Du er Planlægger: du bygger og opgraderer. Makker er Driftsvagt: holder frekvensen, forstærker og har nødknapperne.'
          : 'Du er Driftsvagt: du holder frekvensen, forstærker og har nødknapperne. Makker er Planlægger og bygger.';
      toast.say(`I spiller sammen! ${job} Du er ${look(me)}. Mangler du noget, så ${say}.`, 'good', 7000);
    }
  }

  /** Hvor mange gange et tabt år kan tages om på banen. Orkan: én gang (playtest runde 3: uendelige
   *  omspil gjorde den sværeste sværhed til en øvebane). */
  let retriesLeft = Infinity;

  /** Dagens vagt i gang: planen, mørklagte byer pr. år og den sværhed, spilleren havde valgt før. */
  let daily: { plan: DailyPlan; marks: number[]; prevDiff: DifficultyId } | null = null;

  /** Ud af Dagens vagt: spillerens egen sværhed kommer tilbage. */
  function endDaily(): void {
    if (!daily) return;
    diff = daily.prevDiff;
    daily = null;
  }

  function newGame(id: string, seed = Math.floor(Math.random() * 1_000_000) + 1, isDaily = false): void {
    if (!isDaily) endDaily();
    if (daily) daily.marks = [];
    retriesLeft = diff === 'orkan' ? 1 : Infinity;
    window.clearTimeout(resultTimer);
    window.clearTimeout(attractTimer);
    homeResume = null;
    pendingResume = undefined;
    attract = false;
    started = false;
    tipsDue = 0;
    levelId = id;
    // Spor "sandhed": med Volt spiller man med roller. Første bane lærer at trække en ledning, så
    // der er man altid Planlægger; ellers den rolle, man valgte sidst.
    volt.setRole(soloRole(id));
    game.start(id, diff, seed, daily ? { ...volt.opts(), mod: daily.plan.mod } : volt.opts());
    game.player = volt.on ? volt.seat : 'p1';
    game.rolesLive = volt.on;
    if (import.meta.env.DEV && fixture) applyFixture(game.state, fixture);
    pilot = null;
    pilotUsed = false;
    game.brain = autoplay ? autoplayBrain(autoplay, seed) : null;
    volt.begin();
    teamLog.reset();
    resetHints();
    normalSpeed();
    yearView = null;
    clearLive();
    // En toast fra forrige forsøg må ikke overleve Forfra (PocketGamer, IGN-løkke runde 3).
    toast.clear();
    ring.close();
    bar.pick('build');
    sc.setLevel(game.state, game.level);
    fc.reset();
    juice.reset();
    layout();
  }

  /** Tabt år: spol tilbage til årets start (samme gave, samme frø) i stedet for hele banen forfra. */
  function retryYear(): void {
    const cp = game.checkpoint;
    if (!cp || retriesLeft <= 0) return showLevelStart(levelId);
    retriesLeft--;
    window.clearTimeout(resultTimer);
    if (daily) daily.marks.length = Math.min(daily.marks.length, cp.year);
    const fresh = cloneState(cp);
    // Året tages om fra en fair start: ingen løbende ure og mindst to hjerter.
    retryMercy(fresh, difficulty(fresh.difficulty).trust);
    game.restore(fresh);
    volt.begin();
    teamLog.reset();
    resetHints();
    normalSpeed();
    yearView = null;
    clearLive();
    // En toast fra forrige forsøg må ikke overleve Forfra (PocketGamer, IGN-løkke runde 3).
    toast.clear();
    ring.close();
    bar.pick('build');
    sc.setLevel(game.state, game.level);
    fc.reset();
    juice.reset();
    layout();
    play();
    const last = retriesLeft === 0 ? ' Det er dit eneste omspil.' : '';
    toast.show(`${calendarYear(game.level, cp.year)} igen. Samme vejr, nye valg, og byerne venter 15 s.${last}`, 'good', 3200);
  }

  function play(): void {
    screens.hide();
    attract = false;
    started = true;
    game.held = false;
    game.paused = false;
    stage.focus({ preventScroll: true });
    // Hintet nederst og hånden viser det første træk; ingen ekstra toast oven i.
    adviceKey = '';
    advice = null;
    pauseWhy = '';
    firstBreak = true;
    firstBill = true;
    cityPauses = 0;
    callExplained = false;
  }

  /** Titlens levende baggrund: en ekspert spiller en tilfældig runde bag titlen, stille og roligt. */
  // Er banen startet af spilleren? Ellers er der intet at fortsætte bag titlen.
  let started = false;

  function startAttract(): void {
    window.clearTimeout(attractTimer);
    const seed = Math.floor(Math.random() * 1_000_000) + 1;
    newGame(levelId, seed);
    attract = true;
    // Baggrunden spiller alene, uden roller.
    game.player = 'p1';
    game.rolesLive = false;
    game.brain = autoplayBrain('expert', seed);
    game.held = false;
    game.paused = false;
  }

  /** Titlen: det første, man ser, og hvor HUD'ens hus-knap fører hen. */
  function showTitle(): void {
    window.clearTimeout(resultTimer);
    // Fra en bane i gang: banen venter bag titlen, og Fortsæt tager spilleren tilbage.
    // Også fra pausearket og med en autopilot (?autoplay): kun demoen bag titlen må skiftes ud. IGN
    // runde 2: en bane med autopilot blev erstattet af demoen og "vundet" bag titlen.
    const resumable = started && !attract && (screens.current === null || screens.current === 'pause') && !game.over;
    ring.close();
    controls.select(null);
    if (resumable) {
      game.held = true;
      pendingResume = {
        title: game.level.title,
        go: () => {
          sfx.play('tap');
          homeResume = null;
          pendingResume = undefined;
          screens.hide();
          game.held = false;
          stage.focus({ preventScroll: true });
        },
      };
    } else if (!pendingResume && !attract) {
      startAttract();
    }
    homeResume = pendingResume ? pendingResume.go : null;
    backTo = 'title';
    const first = Object.keys(load().stars).length === 0;
    screens.show(
      'title',
      titleScreen(
        {
          play: () => {
            sfx.play('tap');
            volt.set(params.p2);
            if (first) {
              backTo = 'title';
              showLevelStart(LEVELS[0]?.id ?? levelId);
            } else showHome();
          },
          volt: () => {
            sfx.play('tap');
            volt.set('volt');
            backTo = 'title';
            showLevelStart(first ? (LEVELS[0]?.id ?? levelId) : levelId);
          },
          resume: pendingResume,
          levels: () => {
            sfx.play('tap');
            showHome();
          },
          atlas: () => {
            sfx.play('tap');
            showAtlas();
          },
          settings: () => {
            sfx.play('tap');
            showSettings();
          },
          friend: () => {
            sfx.play('tap');
            endDaily();
            online.open();
          },
        },
        { have: atlasUnlocked().size, total: atlasTotal() },
      ),
    );
  }

  function goBack(): void {
    sfx.play('tap');
    if (backTo === 'pause' && started && !game.over && !attract) showPause();
    else if (backTo === 'home') showHome();
    else if (backTo === 'result' && game.over) showResult();
    else showTitle();
  }

  /**
   * Hus-knappen midt i en bane: et pauseark med Fortsæt forrest, ikke et spring til titlen. Tiden står
   * stille (game.held), til spilleren vælger. Uden en bane i gang går knappen direkte til titlen.
   */
  function showPause(): void {
    if (!started || game.over || attract || (screens.current !== null && screens.current !== 'settings')) {
      showTitle();
      return;
    }
    game.held = true;
    ring.close();
    controls.select(null);
    backTo = 'pause';
    screens.show(
      'pause',
      pauseScreen(
        { level: game.level.title, year: String(calendarYear(game.level, game.state.year)) },
        {
          resume: () => resumeFromPause(),
          settings: () => {
            sfx.play('tap');
            showSettings();
          },
          home: () => {
            sfx.play('tap');
            showTitle();
          },
          tips: () => {
            resumeFromPause();
            openTips();
          },
          ai: pilotCan() && !pilot
            ? () => {
                resumeFromPause();
                startPilot();
              }
            : undefined,
        },
      ),
    );
  }

  function resumeFromPause(): void {
    sfx.play('tap');
    screens.hide();
    game.held = false;
    stage.focus({ preventScroll: true });
  }

  function showSettings(): void {
    backTo = screens.current === 'result' ? 'result' : screens.current === 'home' ? 'home' : screens.current === 'pause' ? 'pause' : 'title';
    screens.show(
      'settings',
      settingsScreen(
        {
          muted,
          vol: volumes(),
          quality: qualityMode,
          qualityNow: quality.name,
          motion,
          systemCalm,
          haptics: audio.haptics,
          canVibrate: typeof navigator.vibrate === 'function',
        },
        {
          muted: (m) => {
            muted = m;
            audio.setMuted(m);
            save({ muted: m });
          },
          volumes: (v) => {
            audio.setVolumes(v);
            save({ vol: v });
          },
          quality: (q) => {
            qualityMode = q;
            quality.setMode(q);
            save({ quality: q });
          },
          motion: (m) => {
            motion = m;
            sc.setMotion(calmNow(), shakeNow());
            document.documentElement.classList.toggle('calm', calmNow());
            save({ motion: m });
          },
          haptics: (on) => {
            audio.haptics = on;
            if (on) audio.vibrate(15);
            save({ haptics: on });
          },
          resetHand: () => {
            resetLessons();
            sfx.play('lesson');
          },
          back: () => goBack(),
        },
      ),
    );
  }

  function showAtlas(): void {
    backTo = screens.current === 'result' ? 'result' : 'title';
    screens.show('atlas', atlasScreen(atlasUnlocked(), () => goBack()));
  }

  function showHome(): void {
    window.clearTimeout(resultTimer);
    game.held = !attract;
    ring.close();
    controls.select(null);
    const resume = pendingResume;
    homeResume = resume ? resume.go : null;
    backTo = 'home';
    // Den valgte bane overlever et skift af sværhed (IGN runde 1: valget sprang tilbage til bane 1).
    let chosen = levelId;
    const render = (): void => {
      screens.show(
        'home',
        homeScreen(daily ? daily.prevDiff : diff, chosen, {
          daily: () => {
            sfx.play('tap');
            backTo = 'home';
            showDaily();
          },
          play: (l) => {
            sfx.play('tap');
            volt.set(params.p2);
            backTo = 'home';
            showLevelStart(l);
          },
          volt: (l) => {
            sfx.play('tap');
            volt.set('volt');
            backTo = 'home';
            showLevelStart(l);
          },
          difficulty: (d, l) => {
            if (daily) daily.prevDiff = d;
            else diff = d;
            chosen = l;
            save({ difficulty: d });
            render();
          },
          resume,
          back: () => {
            sfx.play('tap');
            showTitle();
          },
          friend: () => {
            sfx.play('tap');
            endDaily();
            online.open();
          },
        }),
      );
    };
    render();
  }

  /** Spillerens rolle med Volt på banen. */
  function soloRole(id: string): SoloRole {
    if (getLevel(id).number <= 1) return 'planner';
    return load().voltRole ?? 'planner';
  }

  /** Dagens vagt: dagens bane, frø og modifikator på Vagthavende, ens for alle. */
  function showDaily(): void {
    if (online.active) online.leave();
    const plan = dailyPlan(new Date());
    volt.set(params.p2);
    const prevDiff = daily ? daily.prevDiff : diff;
    daily = { plan, marks: [], prevDiff };
    diff = plan.difficulty;
    newGame(plan.level, plan.seed, true);
    screens.show(
      'levelstart',
      dailyBrief(
        plan,
        game.level,
        {
          go: () => {
            sfx.play('tap');
            play();
          },
          back: () => {
            endDaily();
            goBack();
          },
        },
        // Parret regnes i bidder, mens briefen står (en almindelig bot spiller samme bane, frø og regel).
        { par: dailyPar(plan), streak: streakNow(dailyStreak(), plan.key), playedToday: dailyStreak()?.last === plan.key },
      ),
    );
  }

  function showLevelStart(id: string): void {
    if (online.active) online.leave();
    newGame(id);
    const team: TeamPick | undefined = volt.on
      ? {
          role: volt.playerRole,
          locked: game.level.number <= 1,
          pick: (r) => {
            sfx.play('tap');
            save({ voltRole: r });
            // Samme bane og frø, ny plads: Volt starter forfra i sin nye rolle.
            volt.setRole(r);
            game.player = volt.seat;
            game.rolesLive = true;
            volt.begin();
          },
        }
      : undefined;
    screens.show(
      'levelstart',
      levelStartScreen(
        game.level,
        diff,
        touch,
        {
          go: () => {
            sfx.play('tap');
            play();
          },
          back: () => goBack(),
        },
        team,
      ),
    );
  }

  function showResult(): void {
    ring.close();
    const idx = LEVELS.findIndex((l) => l.id === levelId);
    const run = daily;
    // Dagens vagt har ingen næste bane: den er dagens ene.
    const next = run ? undefined : LEVELS[idx + 1];
    const won = !!game.state.result?.won;
    // Spor "indhold": Dagens vagts gitter med Del, eller kampagneskærmen, når finalen er vundet.
    const extra = run
      ? dailyShare(run.plan, game.level, run.marks, won ? (game.state.result?.stars ?? 0) : 0, `${location.origin}/`, {
          par: dailyPar(run.plan),
          you: { won, stars: won ? (game.state.result?.stars ?? 0) : 0, green: Math.round(game.state.result?.greenPct ?? 0) },
          streak: streakNow(dailyStreak(), run.plan.key),
        })
      : won && levelId === FINALE_LEVEL
        ? campaignDone(game.state.difficulty)
        : null;
    // Klaret bane: rundens Vidste du?-kort lander i Netatlasset.
    // IGN runde 1: kortet stod på tabsskærmen, men låste ikke op. Det, man har læst, ligger nu i atlasset.
    const fact = atlasFact(game.level.id, game.state.seed);
    const newCard = !!(fact && unlockAtlas(atlasId(game.level.id, fact)));
    backTo = 'title';
    screens.show(
      'result',
      resultScreen(
        game.state,
        game.level,
        next !== undefined,
        online.inGame
          ? {
              // Sammen: hvem som helst af jer kan trykke; Prøv igen starter med det samme, Næste bane venter i rummet.
              retry: () => online.again(undefined, true),
              next: () => online.again(next?.id),
              home: () => {
                online.leave();
                started = false;
                showTitle();
              },
              atlas: () => showAtlas(),
            }
          : {
              retry: () => (run ? showDaily() : showLevelStart(levelId)),
              ...(game.checkpoint && retriesLeft > 0 && !game.state.result?.won ? { retryYear: () => retryYear(), retryYearLabel: `Prøv ${calendarYear(game.level, game.checkpoint.year)} igen` } : {}),
              next: () => (next ? showLevelStart(next.id) : showTitle()),
              home: () => showTitle(),
              atlas: () => showAtlas(),
            },
        newCard,
        // IGN-løkke 2, runde 2: begge kan trykke Prøv igen og Næste bane (room-core 'again').
        false,
        // Begge roller får deres regnskab, også når makkeren er Volt (runde 2: "Driftsvagten får
        // ikke anerkendelse på resultatskærmen").
        game.state.roles ? teamLog.view(game.player, nodeName, online.inGame ? undefined : 'Volt') : null,
        extra,
      ),
    );
    volt.logbook(screens.el);
  }

  function togglePause(): void {
    if (online.inGame) return online.togglePause();
    // Årsskiftet må gerne få en pause med: så står spillet stille, når gaven er valgt (IGN runde 1:
    // "gavevalget satte spillet i gang igen"). Andre skærme ejer selv tiden.
    if (game.over || (screens.current && screens.current !== 'yearend')) return;
    sfx.play('tap');
    // En hændelsespause bliver til spillerens egen pause: et tryk på pause må aldrig sætte spillet i
    // gang (IGN runde 1: "jeg trykkede pause, og spillet kørte videre").
    if (pauseWhy && game.paused) {
      pauseWhy = '';
      announce('Pause. Tryk på afspil, når du er klar.');
      return;
    }
    game.paused = !game.paused;
    pauseWhy = '';
    // Orkans plan bygges i det øjeblik, uret går igen, før første tick (Spor: tillid).
    if (!game.paused) controls.flushPlanned();
    announce(game.paused ? 'Pause.' : 'Spillet kører igen.');
  }

  /** IGN-løkke 2, runde 2: "2x overlever Forfra". En ny bane eller et omspil starter altid i normal fart. */
  function normalSpeed(): void {
    if (game.speed === 1) return;
    game.speed = 1;
    save({ speed: 1 });
  }

  function toggleSpeed(): void {
    if (online.inGame) return online.toggleSpeed();
    game.speed = game.speed === 1 ? 2 : 1;
    save({ speed: game.speed });
    announce(game.speed === 2 ? 'Dobbelt fart.' : 'Normal fart.');
  }

  function callYear(): void {
    if (controls.act('call_year')) sfx.play('coin');
  }

  // -------------------------------------------------------------------------
  // Hændelser fra simulationen

  function nodeName(id: string): string {
    return nodeById(game.state, id)?.name ?? '';
  }

  function onEvents(evs: SimEvent[], s: State): void {
    if (evs.length === 0) return;
    sc.events(evs, s);
    audio.events(evs, !!game.brain, attract);
    if (attract) {
      // Baggrunden bag titlen: ingen toasts eller skærme. Når runden slutter, begynder en ny.
      for (const e of evs) {
        if (e.t === 'level_won' || e.t === 'level_lost') {
          window.clearTimeout(attractTimer);
          attractTimer = window.setTimeout(() => {
            if (attract) startAttract();
          }, 3200);
        }
      }
      return;
    }
    for (const e of evs) {
      if (e.t === 'warning' && isCard(e.kind)) deckLog.cards.push({ kind: e.kind, scripted: !!s.threats.find((t) => t.id === e.threat)?.scripted });
      else if (e.t === 'year_end') deckLog.offers.push(e.offer.slice());
      else if (e.t === 'pack_picked') deckLog.picked.push(e.pack);
    }
    volt.events(evs, s);
    if (s.roles) teamLog.events(evs);
    const cut: string[] = [];
    let cutAt: { x: number; y: number } | null = null;
    let undone = false;
    for (const e of evs) {
      switch (e.t) {
        case 'line_removed':
          // Fortryd: sig, hvad det gav tilbage, i stedet for at advare om byen, der mistede ledningen.
          if (e.line === game.undoing) {
            game.undoing = null;
            undone = true;
            if (!game.brain) toast.say(e.refund > 0 ? `Fortrudt. +${e.refund} kr tilbage.` : 'Fortrudt.', 'good', 1800);
          }
          break;
        case 'line_built':
          if (e.by === game.player && !game.brain && !tipsDue && tipsWanted()) tipsDue = performance.now() + 1400;
          // Makkerens ledning dukker bare op på kortet; sig hvem der byggede den (højst hvert 4. sekund).
          if (online.inGame && online.you && e.by !== online.you && performance.now() - partnerToastAt > 4000) {
            const l = s.lines.find((x) => x.id === e.line);
            if (l) {
              partnerToastAt = performance.now();
              toast.show(`Makker byggede ${nodeName(l.a)} til ${nodeName(l.b)}.`, 'good', 1800, sc.lineScreen(e.line));
              announce(`Makker byggede en ledning fra ${nodeName(l.a)} til ${nodeName(l.b)}.`);
            }
          }
          break;
        case 'phantom_vanished':
          if (!game.brain && game.running) {
            const name = nodeName(e.node) || 'Kunden';
            toast.show(e.refund ? `${name} var et fantom. +${e.refund} kr tilbage.` : `${name} var et fantom og er væk igen.`, 'info', 2600, sc.nodeScreen(e.node));
          }
          break;
        case 'node_cut':
          if (e.kind === 'city') {
            cut.push(nodeName(e.node));
            cutAt ??= sc.nodeScreen(e.node);
          }
          break;
        case 'node_appeared':
          // Nye byer dukker op undervejs: sig det, så spilleren ved hvorfor der er en mere.
          if (game.running && e.kind === 'city' && s.tick > 0) {
            // Er spilleren zoomet ind og byen uden for billedet, kan toasten vise hele kortet.
            const p = sc.nodeScreen(e.node);
            const off = !p || p.x < 0 || p.y < 0 || p.x > innerWidth || p.y > innerHeight;
            const act = off && !sc.atHome ? { label: 'Vis', run: () => sc.recenter() } : null;
            const fee = evs.find((x) => x.t === 'connection_fee' && x.node === e.node);
            const paid = fee && fee.t === 'connection_fee' ? ` Den betaler ${fee.amount} kr til ledningen.` : '';
            // Går væk, så snart byen har fået sin ledning (IGN runde 2: forældede beskeder).
            const nid = e.node;
            const unlinked = (): boolean => !game.state.lines.some((l) => l.a === nid || l.b === nid);
            toast.show(`${nodeName(e.node)} vil have strøm.${paid}`, 'info', act ? 3600 : 2600, off ? null : p, act, unlinked);
            announce(`${nodeName(e.node)} er dukket op og vil have strøm.`);
            if (cityPauses < 2 && eventPause(`${nodeName(e.node)} vil have strøm.`)) cityPauses++;
          }
          break;
        case 'line_broken': {
          const l = s.lines.find((x) => x.id === e.line);
          if (l) announce(`Ledningen fra ${nodeName(l.a)} til ${nodeName(l.b)} er knækket. En montør kører derud.`, true);
          if (firstBreak && game.level.mechanics.includes('crew') && !game.brain) {
            firstBreak = false;
            const lid = e.line;
            toast.show('Knækket! Montøren kører selv ud og lapper den.', 'warn', 3600, sc.lineScreen(e.line), null, () =>
              game.state.lines.some((x) => x.id === lid && x.status === 'broken'),
            );
          }
          break;
        }
        case 'line_repaired':
          if (e.cost && firstBill && !game.brain) {
            firstBill = false;
            toast.show(`Lappet for ${e.cost} kr. En forstærket mast knækker ikke, og den koster kun ${REINFORCE_COST} kr.`, 'info', 4200, sc.lineScreen(e.line));
          }
          break;
        case 'clock_started':
          announce(`${nodeName(e.node)} mangler strøm.`);
          break;
        case 'blackout':
          announce(`${nodeName(e.node)} er gået i sort. Tillid tilbage: ${e.trustLeft}.`, true);
          if (e.spared || e.grace) {
            const fix = e.cause === 'lines' ? 'Opgradér ledningen til byen.' : e.cause === 'power' ? 'Byg mere strøm ind.' : 'Træk en ledning til byen.';
            const why = e.spared ? 'men på første bane bliver dit sidste hjerte' : 'men banens første udfald er på huset: hjertet bliver';
            toast.alarm(`${nodeName(e.node)} gik i sort, ${why}. ${fix}`, 4200, sc.nodeScreen(e.node));
          }
          else toast.alarm(`${nodeName(e.node)} gik i sort.`, 2600, sc.nodeScreen(e.node));
          break;
        case 'relit':
          announce(`${nodeName(e.node)} har strøm igen.`);
          break;
        case 'warning': {
          // En ny trussel eller et kort: ringmenuen lukker, så den ikke dækker det nye.
          ring.close();
          const name = THREAT_NAMES[e.kind] ?? 'Uvejr';
          // Toasten er kort; hinten nederst forklarer, hvad man gør.
          // Med roller skal begge handle på sekunder (IGN runde 4): Driftsvagten forstærker, Planlæggeren bygger udenom.
          const plan = game.rolesLive && game.player === 'p1';
          const flywheel = game.level.mechanics.includes('flywheel');
          const what =
            e.kind === 'duo'
              ? 'I skal begge nå jeres del. Se kortet.'
              : e.kind === 'dunkelflaute'
              ? game.level.mechanics.includes('norway')
                ? 'Vind og sol går i stå. Ring til Norge!'
                : 'Vind og sol går i stå. Lad batterierne stå klar.'
              : e.kind === 'cable_half'
              ? 'Kablet bærer kun det halve i 20 s. Byg en omvej.'
              : e.kind === 'plant_trip'
              ? plan
                ? 'Et værk falder ud. Makker tager svinghjul og Lynreserve!'
                : flywheel
                ? 'Byg et svinghjul, eller tryk Lynreserve.'
                : 'Tryk Lynreserve, når det sker.'
              : e.kind === 'storm'
              ? game.rolesLive
                ? plan
                  ? 'Byg en omvej uden om stormen. Makker forstærker!'
                  : 'Forstærk masterne nu. Makker bygger en omvej!'
                : 'Tryk Forstærk i værktøjslinjen.'
              : isCard(e.kind)
                ? 'Vælg et svar på kortet.'
                : plan
                  ? 'Driftsvagten jager den væk. Ping, hvis makker ikke ser den.'
                  : 'Tryk på den for at jage den væk.';
          // Kortet er selv beskeden; en toast oven i ville bare dække mere af kortet.
          // Går væk, når truslen er forbi varslet, eller når alle master er forstærket.
          const tid = e.threat;
          const storm = e.kind === 'storm';
          const live = (): boolean =>
            game.state.threats.some((t) => t.id === tid && t.phase === 'warning') && (!storm || exposedLines(game.state).length > 0);
          if (!isCard(e.kind) && e.kind !== 'duo') toast.show(`${name} på vej. ${what}`, 'warn', 3200, sc.threatScreen(e.threat), null, live);
          announce(`${name} på vej. ${what}`, true);
          eventPause(`${name} på vej.`);
          break;
        }
        case 'card_answered':
          // Hånden viser kun det allerførste kort, også når svaret blev valgt for en.
          if (!game.brain && !lessonDone('card')) markLesson('card');
          if (!game.brain) toast.show(`${THREAT_NAMES[e.kind] ?? 'Kortet'}: ${CARD_OPTION[e.answer].label.toLowerCase()}${e.auto ? ', valgt for dig' : ''}.`, 'info', 2200);
          break;
        case 'power_used':
          announce(`${POWER_TEXT[e.power].label}.`);
          break;
        case 'threat_shooed': {
          // Playtest runde 3: advarslen ("på vej, tryk på den") blev stående efter tappet, og
          // kvitteringen kom aldrig frem. Advarslen går, og kvitteringen nævner gæsten.
          toast.drop(`${THREAT_NAMES[e.kind] ?? 'Uvejr'} på vej. Tryk på den for at jage den væk.`);
          if (!game.brain) {
            const who = guestName(s, e.threat);
            toast.say(`${who.charAt(0).toUpperCase()}${who.slice(1)} er jaget væk.`, 'good', 1600);
          }
          break;
        }
        case 'threat_hit': {
          // Stormsikker: stormen ramte, og alle de master, den ramte, var forstærket. Det er
          // øjeblikket, hvor forberedelsen betaler sig; det skal kunne mærkes (playtest runde 3).
          const saved = evs.filter((x) => x.t === 'line_saved').length;
          if (e.kind === 'storm' && e.lines.length === 0 && saved > 0 && !game.brain) {
            const bill = (difficulty(s.difficulty).repairKr ?? 0) > 0;
            toast.say(`Stormsikker! ${saved === 1 ? 'Masten' : 'Masterne'} holdt${bill ? ', og der kom ingen regning' : ''}.`, 'good', 2400);
          }
          break;
        }
        case 'freq_dip': {
          // Frekvensen: over 49,5 Hz holdt nettet roligt (bonus), under 49,0 kobler nettet selv en by fra.
          const hzTxt = hz(e.nadir);
          if (e.shed) {
            const txt = `${nodeName(e.node)} faldt ud. Frekvensen ramte ${hzTxt}, så ${nodeName(e.shed)} blev koblet fra et øjeblik.`;
            if (!game.brain) {
              toast.alarm(txt, 3200, sc.nodeScreen(e.shed));
              sfx.play('error');
            }
            announce(txt, true);
          } else {
            const calm = e.nadir >= CALM_CHZ;
            const txt = calm ? `${nodeName(e.node)} faldt ud, men frekvensen holdt: ${hzTxt}.` : `${nodeName(e.node)} faldt ud. Frekvensen dykkede til ${hzTxt}, men ingen by røg.`;
            if (!game.brain) {
              toast.say(calm ? `${txt} +${RIDE_BONUS} kr` : txt, calm ? 'good' : 'warn', 2600);
              sfx.play(calm ? 'ding' : 'warn');
            }
            announce(txt);
          }
          break;
        }
        case 'hz_out': {
          // Spor: tillid. Nålen gik ud over 200 mHz: kun Driftsvagten (den, der kan regulere) hører det,
          // og højst hvert 30. sekund, så en travl vagt ikke drukner i det.
          if (game.brain || powers.hzCenter() === null || (s.tick >= hzToldAt && s.tick - hzToldAt < 300)) break;
          hzToldAt = s.tick;
          const txt = `Frekvensen er ${hzText(e.dev)}. Tryk Frekvens, før reserven koster mere.`;
          toast.say(txt, 'warn', 2600);
          announce(txt);
          break;
        }
        case 'hz_trim':
          if (e.by === game.player && e.bonus > 0 && !game.brain) announce(`Frekvensen er reguleret. +${e.bonus} kr.`);
          break;
        case 'duo_step': {
          const mine = e.by === game.player;
          const who = e.role === 'drift' ? 'Driftsvagten' : 'Planlæggeren';
          const did = e.role === 'drift' ? 'forstærket masten' : 'opgraderet ledningen';
          const t = s.threats.find((x) => x.id === e.threat);
          const both = !!t?.duo?.plan && !!t.duo.drift;
          if (!game.brain) {
            if (both) toast.say('Begge dele er gjort. Nu må lynet bare komme!', 'good', 2200);
            else toast.say(mine ? `Godt! Nu mangler ${e.role === 'drift' ? 'Planlæggeren' : 'Driftsvagten'}.` : `${who} har ${did}. Din tur!`, 'good', 2200);
          }
          announce(`${who} har ${did}.`);
          if (!mine) sfx.play('ding');
          break;
        }
        case 'duo_won': {
          if (!game.brain) {
            juice.pop(() => sc.lineScreen(e.line), `SAMMEN! +${e.bonus}`, 'lyn', 1600);
            hud.gain(`+${e.bonus} kr`);
            toast.say(`I klarede lynet sammen! Ledningen holdt. +${e.bonus} kr`, 'good', 2800);
            sfx.play('fanfare');
          }
          announce(`I klarede det fælles lyn sammen. ${e.bonus} kroner i kassen.`);
          break;
        }
        case 'duo_missed': {
          const txt = e.done === 1 ? 'Lynet slog ned. Kun den ene del nåede frem, så ledningen bærer det halve, til en montør har set på den.' : 'Lynet slog ned, og ledningen knækkede. Næste gang: én forstærker, én opgraderer.';
          if (!game.brain) toast.say(txt, 'warn', 3200);
          announce(txt);
          break;
        }
        case 'ride_bonus':
          if (!game.brain) {
            hud.gain(`+${e.amount} kr`);
            if (e.kind !== 'plant_trip') toast.say(`Holdt! Nettet bar hele bølgen. +${e.amount} kr`, 'good', 2000);
          }
          break;
        case 'year_end':
          if (daily) daily.marks[e.year] = e.stats.blackouts;
          // Tom gavepose (gaverne trækkes uden tilbagelægning): simulatoren er allerede gået videre til
          // næste år, så her er intet at vælge. Et kort uden gaver låste spillet (IGN-løkke 3).
          if (e.offer.length === 0) {
            if (!game.brain) toast.say(`Årsskifte: ${num(e.income)} kr i indtægt. Gaveposen er tom i år.`, 'good', 2400);
            break;
          }
          // Midt i et træk venter skærmen, til fingeren slipper (solo står spillet stille imens).
          // Ellers åd årsskiftet trækket (playtest), og ledningen bygges nu efter gaven.
          if (controls.holding) {
            pendingYear = { s, year: e.year, stats: e.stats };
            if (!online.inGame && !game.remote && !game.brain) game.held = true;
          } else showYearEnd(s, e.year, e.stats);
          break;
        case 'advance':
          if (!game.brain) hud.gain(`+${e.amount} kr`);
          break;
        case 'income_tick': {
          // Mønterne flyver fra byerne til kassen, og kassen ruller op, når de lander. Rolig bevægelse:
          // ingen mønter, kassen blinker (hud.calm).
          if (calmNow() || screens.current !== null) break;
          const to = hud.coinTarget();
          if (!to) break;
          const from = e.from.map((id) => sc.nodeScreen(id)).filter((p): p is { x: number; y: number } => p !== null && Number.isFinite(p.x));
          if (juice.coins(from, to) > 0) hud.holdRoll(COIN_MS);
          break;
        }
        case 'rescue':
          juice.pop(() => sc.nodeScreen(e.node), 'PUH!', 'puh', 1100);
          if (!game.brain) announce(`${nodeName(e.node)} fik strøm i sidste øjeblik.`);
          break;
        case 'quick_connect':
          juice.pop(() => sc.nodeScreen(e.node), `LYN! +${e.bonus}`, 'lyn', 900);
          if (!game.brain) announce(`Lyn-tilslutning! Byen fik strøm hurtigt: ${e.bonus} kr i bonus.`);
          break;
        case 'connection_fee':
          if (!game.brain) {
            hud.gain(`+${e.amount} kr`);
            announce(`${nodeName(e.node)} betaler tilslutningsbidrag: ${e.amount} kroner.`);
          }
          break;
        case 'crew_hired':
          if (!game.brain) {
            bar.kick('hire');
            toast.say('Ny montør hyret. Holdet er klar om lidt.', 'good', 1800);
          }
          break;
        case 'pack_picked':
          if (screens.current === 'yearend') screens.hide();
          yearView = null;
          pendingYear = null;
          if (yearHeld) {
            yearHeld = false;
            game.held = false;
          }
          // Trækket, der sluttede, mens årsskiftet ventede, bygges nu.
          controls.flushAfterYear();
          // Begge gaver på én gang: to hændelser, én besked (den anden overskriver den første).
          if (e.both !== undefined) {
            // Spor: tillid (co-op runde 4): kassen er fælles, så den ene kan købe begge. Sig hvem.
            const who = online.inGame && e.by && e.by !== 'both' ? (e.by === game.player ? 'Du købte begge' : 'Makker købte begge') : 'Begge';
            const text = `${who} gaver for ${e.both} kr. Godt nytår.`;
            toast.show(text, 'good', 2400);
            if (e.pack === game.state.lastOffer[0]) announce(text);
          } else if (online.inGame && e.by && game.state.coop) {
            // Med roller (IGN-løkke runde 4): sig, hvordan gaven blev valgt, på begge skærme.
            const name = PACKS[e.pack].name;
            const text =
              e.by === 'both' ? `I var enige: ${name}.` : e.by === game.player ? `${name}. Du afgjorde det.` : `${name}. Makker afgjorde det.`;
            toast.show(text, 'good', 2800);
            announce(text);
          } else if (online.inGame && !e.auto && performance.now() - myPackAt > 6000) {
            toast.show(`Makker valgte ${PACKS[e.pack].name}.`, 'good', 2600);
            announce(`Din makker valgte ${PACKS[e.pack].name}.`);
          } else toast.show(`${PACKS[e.pack].name}${e.auto ? ' blev valgt for dig.' : '.'}`, 'good', 2400);
          break;
        case 'pack_vote':
          yearView?.update(s);
          // Solo med Volt og roller: gaven er spillerens valg, og Volt siger enig med det samme, så
          // ingen venter på en stemme fra makkeren.
          if (!online.inGame && volt.on && game.rolesLive && e.player === game.player && s.phase === 'yearend' && s.packVotes?.[volt.voltSeat] === undefined) {
            game.pushAs(volt.voltSeat, 'pick_pack', undefined, String(e.index), 'bot');
          }
          if (online.inGame && e.player !== game.player && s.phase === 'yearend') {
            const pack = s.packOffer[e.index];
            const name = pack ? PACKS[pack].name : 'en gave';
            toast.show(`Makker stemmer på ${name}. Enig? Tryk på samme gave.`, 'info', 3000);
            announce(`Din makker stemmer på ${name}.`);
            sfx.play('ding');
          }
          break;
        case 'year_start':
          // Spor: kortet: et nyt år må ramme nettet ind igen (blødt, når spilleren har sluppet kameraet).
          // Makkerrum: intet uanmeldt zoom ved årsskiftet (spor "coop"); kun nye byer uden for billedet flytter kameraet.
          sc.newYear(online.inGame);
          // Solo: gem årets start, så et tabt år kan prøves igen derfra.
          if (!online.inGame && !game.remote && !game.brain && !attract) game.checkpoint = cloneState(s);
          if (e.finale) toast.show('Finalen. Hold lyset tændt til sidst.', 'warn', 3200);
          else announce(`Nyt år: ${calendarYear(game.level, e.year)}.`);
          break;
        case 'level_won':
        case 'level_lost':
          finish(s, e.t === 'level_won');
          break;
        case 'rejected':
          if (e.player === game.player && !game.brain) controls.fail(e.kind, e.error);
          break;
        default:
          break;
      }
    }
    // En by mistede forbindelsen (brud eller nedrivning): sig hvilken, med det samme.
    if (cut.length > 0 && !undone && game.running && !game.brain) {
      sfx.play('warn');
      const who = cut.length === 1 ? `${cut[0]} er` : `${cut.length} byer er`;
      toast.alarm(`${who} skåret fra nettet.`, 2600, cutAt);
    }
  }

  // Årsskiftet: 1, 2 (og 3) vælger gaven, som tallene på kortene viser.
  window.addEventListener('keydown', (e) => {
    if (screens.current !== 'yearend' || e.ctrlKey || e.metaKey || e.altKey) return;
    const i = Number(e.key) - 1;
    // 3: begge gaver, når der er to at vælge imellem.
    if (i === 2 && game.state.packOffer.length === 2) {
      e.preventDefault();
      pickBoth();
      return;
    }
    if (!Number.isInteger(i) || i < 0 || i >= game.state.packOffer.length) return;
    e.preventDefault();
    pickPack(i);
    sfx.play('pop');
  });

  /** Vælg en gave. Tidspunktet huskes, så et makkervalg kan siges som "Makker valgte ...". */
  function pickPack(i: number): void {
    myPackAt = performance.now();
    game.push('pick_pack', undefined, String(i));
  }

  /** Begge gaver for prisen (spor "sværhed"). Kassen er fælles, så den ene kan købe for begge. */
  function pickBoth(): void {
    myPackAt = performance.now();
    if (game.push('pick_both').ok) sfx.play('coin');
  }

  function showYearEnd(s: State, year: number, stats: YearSummary): void {
    // Online kan samme årsskifte komme flere gange (genspil efter en resync): ét kort pr. år.
    if (screens.current === 'yearend' && yearView && yearShown === year) {
      yearView.update(s);
      return;
    }
    yearShown = year;
    dropEventPause();
    ring.close();
    controls.select(null);
    // Solo venter gaven på spilleren: ingen nedtælling, og verden står stille imens (playtest: "vælger
    // selv"). Online og med Volt tæller den ned, for rummet kan ikke vente på én spiller.
    const timed = online.inGame || !!game.remote || !!game.brain || attract;
    if (!timed) {
      yearHeld = true;
      game.held = true;
    }
    yearView = yearEndScreen(
      s,
      calendarYear(game.level, year),
      stats,
      (i) => {
        pickPack(i);
        sfx.play('pop');
      },
      online.inGame ? { timed, you: game.player, both: pickBoth } : volt.on ? { timed, both: pickBoth, bid: 'Volt' } : { timed, both: pickBoth },
    );
    yearView.update(s);
    screens.show('yearend', yearView.el);
    announce(`Årsskifte. ${num(s.lastIncome)} kroner i indtægt, ${num(s.budget)} kroner i kassen. Vælg en gave.`);
  }

  function finish(s: State, won: boolean): void {
    const stars = s.result?.stars ?? 0;
    if (daily) {
      // Dagens vagt tæller i dagens gitter, ikke i kampagnens stjerner (modifikatoren gør den anderledes).
      const years = game.level.years;
      if (daily.marks[s.year] === undefined && s.year < years) daily.marks[s.year] = s.yearStats.blackouts;
      const played = won ? years : Math.min(s.year + 1, years);
      const got = daily.marks;
      daily.marks = Array.from({ length: played }, (_, i) => got[i] ?? 0);
      recordDaily(daily.plan.key, { stars: won ? stars : 0, won, marks: daily.marks, green: Math.round(s.result?.greenPct ?? 0) });
    } else if (won && s.result && !pilotUsed) recordStars(levelId, s.difficulty, s.result.stars);
    stopPilot(true);
    announce(won ? `Banen er klaret med ${stars} ${stars === 1 ? 'stjerne' : 'stjerner'}.` : 'Tilliden slap op. Prøv igen.', true);
    if (screens.current === 'yearend') screens.hide();
    // Banen er slut: ingen menu, intet valg og ingen træk-boble hænger ved over resultatkortet.
    ring.close();
    controls.select(null);
    bubble.hidden = true;
    window.clearTimeout(resultTimer);
    // Lad lyset tænde (eller slukke) et øjeblik, før skærmen kommer. Ved sejr når fyrværkeriet op.
    resultTimer = window.setTimeout(showResult, won ? 2600 : 1400);
  }

  // -------------------------------------------------------------------------
  // Hint i bunden: det vigtigste at gøre lige nu.

  // Rådet kommer fra træneren (ai/coach.ts), så hint, hånd og balance-målingens "nybegynder"
  // peger på det samme.
  let hintAt = 0;
  /** Byer, vi allerede har sagt "brug pengene" om, mens de er mørke. */
  const nudged = new Set<string>();
  /** Rådet lige nu, og hvordan sværheden vil have det vist: hånd og hint, eller kun en ring om målet. */
  let advice: Shown | null = null;
  let adviceKey = '';
  let adviceSince = 0;
  let drag: { from: string; to: string } | null = null;
  let firstBreak = true;
  /** Første reparationsregning på banen forklares; siden står kun "-5 kr" ved ledningen. */
  let firstBill = true;
  /** Lærling pauser kun ved de to første nye byer pr. bane: siden brød det flowet (playtest runde 3). */
  let cityPauses = 0;
  let partnerToastAt = -Infinity;
  let myPackAt = -Infinity;
  let callExplained = false;

  function adviceTarget(a: Advice): string {
    if (a.kind === 'build') return `${a.from}|${a.to}`;
    if ('line' in a && a.line) return a.line;
    return 'threat' in a ? a.threat : 'node' in a ? (a.node ?? '') : '';
  }

  /** Alle id'er et råd handler om (til makkerens "Jeg tager den"). */
  function adviceTargets(a: Advice): string[] {
    if (a.kind === 'build') return [a.from, a.to];
    const t = adviceTarget(a);
    return t ? [t] : [];
  }

  /** Det råd, hintet viser nu, hvornår det kom, og hvor mange handlinger spilleren havde gjort da. */
  let heldAt = 0;
  /** Online: rådet holdes til næste 2,5 s-skel i simulationens tid. */
  const HOLD_TICKS = 25;
  /** Et råd, der kan vente: ingen by mangler strøm lige nu, det er pynt på et net, der kører. */
  const quietTip = (a: Advice): boolean =>
    !a.urgent && (a.kind === 'battery' || (a.kind === 'build' && (a.why === 'second' || a.why === 'producer')) || (a.kind === 'upgrade' && !a.node));
  const TIP_STAND_MS = 40_000;
  const TIP_REST_MS = 90_000;
  const tipRest = new Map<string, number>();
  let heldActs = 0;
  /** Hintene, der er kommet frem på denne bane (testkrog og målinger). */
  const hintLog: { tick: number; year: number; text: string; urgent: boolean; ring?: boolean }[] = [];
  /** Indholdtestens krog: kortene og gaverne på banen, så "intet kort eller gave to gange" kan måles i browseren. */
  const deckLog = { cards: [] as { kind: ThreatKind; scripted: boolean }[], offers: [] as PackId[][], picked: [] as PackId[] };
  let hintLast = '';
  let ringLast = '';
  function resetHints(): void {
    hintLog.length = 0;
    deckLog.cards.length = 0;
    deckLog.offers.length = 0;
    deckLog.picked.length = 0;
    hintLast = '';
    ringLast = '';
    hintBook.clear();
    bookLast = '';
    bookShowing = '';
  }

  /**
   * Spor: tillid. Hintbogen: hvert træner-hint (tal skrevet om til #, "Pause. " skåret af) kommer frem
   * højst HINT_MAX gange pr. bane, og et hint, der er blevet afløst af et andet, kommer ikke igen, før
   * spilleren har gjort noget. IGN runde 4 talte "Dit store batteri venter" 33 gange på bane 6.
   * Forsvinder hintet kort (en åben ringmenu, en pause) og kommer det samme igen inden for
   * HINT_GAP_MS, er det samme visning.
   */
  const HINT_MAX = 3;
  const HINT_GAP_MS = 6000;
  const hintBook = new Map<string, { n: number; acts: number }>();
  /** Det seneste træner-hint, der stod fremme, og det, der står nu (tom: intet træner-hint). */
  let bookLast = '';
  let bookShowing = '';
  let bookGoneAt = 0;
  const hintKey = (t: string): string => t.replace(/^Pause\. /, '').replace(/\d+/g, '#');

  /** Må træner-hintet stå? 'new' når det kommer frem på ny (og tælles), 'on' når det allerede står. */
  function hintBookFor(text: string, now: number): 'new' | 'on' | 'no' {
    const k = hintKey(text);
    if (k === bookShowing) return 'on';
    const e = hintBook.get(k);
    if (e && k === bookLast && now - bookGoneAt < HINT_GAP_MS) {
      bookShowing = k;
      return 'on';
    }
    // Online er rummets optæller fælles, så begge skærme tier og taler på samme tid.
    const acts = game.remote ? game.opN : game.acts;
    if (e && (e.n >= HINT_MAX || (k !== bookLast && e.acts === acts))) return 'no';
    hintBook.set(k, { n: (e?.n ?? 0) + 1, acts });
    bookShowing = k;
    bookLast = k;
    return 'new';
  }

  /** Hintet om en by, der ikke kan komme på, fordi naboen er fuld (alle sværheder, også i pausen). */
  function blockedHint(s: State, b: NonNullable<ReturnType<typeof blockedCity>>): { text: string; onTap: (() => void) | null } {
    const mine = !game.rolesLive || roleOf('upgrade_station') === game.player;
    return {
      text: mine ? blockedText(s, b) : `${online.inGame ? 'Planlæggeren' : 'Volt'}s job: ${blockedText(s, b)}`,
      onTap: mine ? () => controls.openNode(b.full) : null,
    };
  }

  /** Første ledning: navngiv en producent, kortet faktisk har (IGN runde 4: vindmølle-hint uden mølle). */
  function firstLineText(s: State): string {
    const has = (k: (n: GridNode) => boolean): boolean => s.nodes.some((n) => n.active && !n.phantom && k(n));
    const what = has((n) => n.kind === 'wind' || n.kind === 'offshore') ? 'en vindmølle' : has((n) => n.kind === 'solar') ? 'en solpark' : has((n) => n.kind === 'plant') ? 'et værk' : '';
    if (!what) return '';
    return touch ? `Træk med fingeren fra en by til ${what}.` : `Træk fra en by til ${what}.`;
  }

  /** Rådet i pausen: "om et øjeblik" passer ikke, mens uret står stille (IGN runde 4). */
  /** Det nye års venten i makkerrummet: hvem der er klar, og hvornår året starter af sig selv. */
  function yearWaitText(w: NonNullable<typeof game.yearWait>, now: number): string {
    const me = w.ready.includes(game.player);
    const mate = w.ready.some((p) => p !== game.player);
    const left = w.goAt === null ? 0 : Math.max(1, Math.ceil((w.goAt - now) / 1000));
    if (me) return `Du er klar. Året starter, når makker trykker afspil${left ? `, senest om ${left} s` : ''}.`;
    if (mate) return `Makker er klar. Tryk afspil${left ? `, ellers starter året om ${left} s` : ''}.`;
    return 'Nyt år. Uret står, til I begge trykker afspil.';
  }

  function pausedText(s: State, a: Advice): string {
    if (a.kind === 'calm' && a.soon && !a.lack) return `Strømmen er på vej til ${nodeName(a.node)}. Den tænder, når tiden går igen.`;
    return adviceText(s, a, touch);
  }

  function sameAdvice(a: Advice, b: Advice): boolean {
    return a.kind === b.kind && adviceTarget(a) === adviceTarget(b);
  }

  /** Passer rådet stadig? Ellers må hintet skifte med det samme. */
  function stillGood(s: State, a: Advice): boolean {
    switch (a.kind) {
      case 'praise':
      case 'calm':
        return true;
      case 'save':
        return s.budget < a.need;
      case 'broken':
        return s.lines.some((l) => l.id === a.line && l.status === 'broken');
      default:
        return adviceIntent(s, a) !== null;
    }
  }

  /**
   * Hysterese: et råd står i mindst 2,5 s, så hint og hånd ikke blafrer, når to råd er lige gode.
   * Det skifter før, når spilleren har gjort noget, når det gamle ikke længere kan gøres, eller når
   * noget hastende (brud, storm, kort) kommer.
   */
  function pickAdvice(s: State, now: number): Shown | null {
    // Spor "sandhed": hintet kommer fra samme sandhed som bykortet og Volt (src/ai/truth.ts). Med
    // roller (makker eller Volt) får Planlæggeren byggerådene og Driftsvagten storm og kort først; er
    // rådet den andens job, får Driftsvagten sit eget, hvis der er et, ellers peger hintet på makkeren.
    const raw: Shown | null = screens.current || game.over || game.brain ? null : hintFor(s, { player: game.player, roles: game.rolesLive });
    // Har makkeren sagt "Jeg tager den" til rådets mål, peger hintet ikke på det (IGN-løkke runde 1).
    const fresh = raw && online.claimed(adviceTargets(raw)) ? null : raw;
    if (advice && fresh && sameAdvice(advice, fresh)) return fresh;
    // Spor: tillid. Online regnes hysteresen i rummets tid: rådet må skifte ved hvert 2,5 s-skel i
    // simulationen, og "spilleren har gjort noget" er rummets optæller. Så skifter hintet samtidig på
    // begge skærme, og de viser det samme (co-op runde 4).
    const shared = !!game.remote;
    const at = shared ? Math.floor(s.tick / HOLD_TICKS) : now;
    const acts = shared ? game.opN : game.acts;
    const keep =
      advice !== null &&
      !screens.current &&
      !game.over &&
      !game.brain &&
      (shared ? at === heldAt : now - heldAt < 2500) &&
      acts === heldActs &&
      !(fresh?.urgent && !advice.urgent) &&
      stillGood(s, advice);
    if (keep) return advice;
    heldAt = at;
    heldActs = acts;
    return fresh;
  }

  function updateHint(now: number): void {
    if (now - hintAt < 250) return;
    hintAt = now;
    const s = game.state;
    let text = '';
    let urgent = false;
    let onTap: (() => void) | null = null;
    let blk: ReturnType<typeof blockedCity> = null;
    /** Et træner-hint (ikke et svar på det, spilleren er i gang med): det går gennem hintbogen. */
    let coach = false;
    advice = pickAdvice(s, now);
    const key = advice ? `${advice.kind}:${adviceTarget(advice)}` : '';
    if (key !== adviceKey) {
      adviceKey = key;
      adviceSince = now;
    }
    // Et roligt tip (batteri, vej nr. 2, en ubrugt mølle), der har stået 40 s uden at blive taget,
    // hviler i 90 s. IGN runde 2: "Dit store batteri venter" stod der gennem to år. Hastende råd
    // hviler aldrig.
    if (advice && quietTip(advice)) {
      if (now - adviceSince > TIP_STAND_MS) {
        tipRest.set(key, now + TIP_REST_MS);
        adviceSince = now + TIP_REST_MS;
      }
      if ((tipRest.get(key) ?? 0) > now) advice = null;
    }
    // Hånden trækker altid mellem de samme to noder, som teksten nævner. På de svære sværheder er
    // der kun en ring om målet: ingen hånd, intet spor mellem noderne og ingen tekst om rådet.
    const ringOnly = advice?.display === 'ring';
    drag = advice && !ringOnly && !advice.partner ? adviceDrag(advice) : null;
    moneyNudge();
    // Mens ringmenuen er åben, taler den selv; hintet ville ligge under titlen.
    if (pauseWhy && (!game.paused || now > pauseEnd || game.acts !== pauseActs)) resumeEventPause();
    if (screens.current || game.over || ring.isOpen || focus.quiet) text = '';
    else if (game.paused && game.yearWait) {
      // Spor: tillid (co-op): det nye år venter på jer begge. Rådet står med, så I ved, hvad der venter.
      text = `${yearWaitText(game.yearWait, now)}${advice && !ringOnly && !advice.partner ? ` ${pausedText(s, advice)}` : ''}`;
      urgent = true;
      onTap = advice && !advice.partner ? hintAction(advice) : null;
    }
    else if (game.paused && pauseWhy && ringOnly) {
      text = `${pauseWhy} Spillet venter lidt på dig.`;
      urgent = true;
    }
    else if (game.paused && pauseWhy) {
      coach = !!advice?.urgent;
      text = advice?.urgent ? `Pause. ${pausedText(s, advice)}` : `${pauseWhy} Spillet venter lidt på dig.`;
      urgent = true;
      onTap = advice ? hintAction(advice) : null;
    }
    // Spor: tillid. I pausen står et rigtigt råd før pausepillen: først den fulde nabo (IGN runde 4:
    // "i pausepillens sted og ikke under den"), så trænerens råd, og først derefter pillen.
    else if (game.paused && !controls.dragging && !sc.selected && bar.tool !== 'upgrade' && bar.tool !== 'station' && (blk = blockedCity(s))) {
      ({ text, onTap } = blockedHint(s, blk));
      urgent = true;
      coach = true;
    }
    else if (game.paused && !controls.dragging && !sc.selected && bar.tool !== 'upgrade' && bar.tool !== 'station' && advice && !ringOnly) {
      text = `Pause. ${pausedText(s, advice)}`;
      urgent = advice.urgent;
      onTap = hintAction(advice);
      coach = true;
    }
    else if (game.paused && !difficulty(s.difficulty).buildInPause) {
      text = 'Pause. Træk dine næste ledninger; de bygges, når tiden går igen.';
      coach = true;
    }
    else if (game.paused && !controls.dragging && !sc.selected && bar.tool !== 'upgrade' && bar.tool !== 'station') text = 'Pause. Tryk på afspil for at fortsætte.';
    else if (controls.dragging) {
      const to = drag && sc.selected === drag.from ? nodeById(s, drag.to) : undefined;
      text = to ? `Slip på ${to.name}.` : 'Slip på en mølle eller by.';
    } else if (sc.selected) {
      const to = drag && sc.selected === drag.from ? nodeById(s, drag.to) : undefined;
      text = to ? `Tryk på ${to.name}, så går ledningen fra ${nodeName(sc.selected)}.` : `Tryk på en by eller mølle. Ledningen går fra ${nodeName(sc.selected)}.`;
    }
    else if (bar.tool === 'upgrade') text = 'Tryk på en ledning for at opgradere den.';
    else if (bar.tool === 'station') text = 'Tryk på en by eller mølle for at give den flere pladser.';
    else if (advice?.partner) {
      const a = advice;
      if (online.inGame) {
        const who = game.player === 'p1' ? 'Driftsvagten' : 'Planlæggeren';
        text = `${who}s job: ${adviceText(s, a, touch)} Tryk for at vise makker stedet.`;
        onTap = () => {
          const at = adviceTarget(a).split('|').pop() ?? '';
          if (at) online.pingAt(game.player === 'p2' ? 'build' : a.urgent ? 'danger' : 'help', at);
        };
      } else {
        // Solo med Volt: det er hans træk, og han tager det. Intet at trykke på.
        text = `Volts job: ${adviceText(s, a, touch)}`;
      }
      urgent = false;
      coach = true;
    }
    // IGN-løkke 2, runde 3: en ny by, der ikke kan komme på, fordi naboen er fuld, siges på alle
    // sværheder, også når træneren tier (Systemansvarlig, Orkan). Advarslen navngiver den fulde node.
    else if ((blk = !advice || ringOnly || (advice.kind === 'save' && advice.what === 'station') ? blockedCity(s) : null)) {
      ({ text, onTap } = blockedHint(s, blk));
      urgent = true;
      coach = true;
    }
    else if (advice && !ringOnly) {
      text = adviceText(s, advice, touch);
      urgent = advice.urgent;
      onTap = hintAction(advice);
      coach = true;
    } else if (s.lines.every((l) => l.owner === 'level')) {
      text = firstLineText(s);
      coach = true;
    }
    // Hintbogen: et træner-hint, der har været fremme nok, eller som kommer igen uden at spilleren
    // har gjort noget, bliver væk. Hånden og ringen viser stadig målet.
    let fresh = false;
    if (coach && text) {
      const b = hintBookFor(text, now);
      if (b === 'no') {
        text = '';
        urgent = false;
        onTap = null;
        blk = null;
      } else fresh = b === 'new';
    }
    if (!(coach && text) && bookShowing) {
      bookShowing = '';
      bookGoneAt = now;
    }
    toast.setHint(text, urgent, onTap);
    if (text !== hintLast) {
      hintLast = text;
      // Loggen tæller visninger, som spilleren ser dem: et træner-hint, når bogen tæller det.
      if (text && (!coach || fresh)) hintLog.push({ tick: s.tick, year: s.year, text, urgent });
    }
    dodgeHint(text !== '' && advice ? advice : null);
    sc.named = new Set(blk ? [blk.city, blk.full] : text !== '' && advice ? adviceNodes(advice) : []);
  }

  /** Hintets plads nederst, målt mens det stod der. */
  let hintHome: DOMRect | null = null;

  /**
   * Står rådets mål under hintet nederst, flytter hintet op under vejrudsigten. Playtest: Erritsø stod
   * bag panelet, mens coachen bad om en ledning dertil.
   */
  function dodgeHint(a: Advice | null): void {
    const el = toast.hint;
    const dodged = el.classList.contains('dodge');
    if (!dodged) {
      const r = el.getBoundingClientRect();
      if (r.height > 0) hintHome = r;
    }
    let hit = false;
    if (a && hintHome && !cards.isOpen) {
      const r = hintHome;
      for (const id of adviceNodes(a)) {
        const p = sc.nodeScreen(id);
        if (p && p.x > r.left - 40 && p.x < r.right + 40 && p.y > r.top - 50 && p.y < r.bottom + 30) hit = true;
      }
    }
    el.classList.toggle('dodge', hit);
  }

  /** Noderne, et råd handler om (begge ender af en ledning). */
  function adviceNodes(a: Advice): string[] {
    const t = adviceTarget(a);
    if (t.includes('|')) return t.split('|');
    const l = game.state.lines.find((x) => x.id === t);
    if (l) return [l.a, l.b];
    const th = game.state.threats.find((x) => x.id === t);
    if (th) {
      const tl = game.state.lines.find((x) => x.id === th.targetLine);
      return tl ? [tl.a, tl.b] : (th.nodes ?? []);
    }
    return t ? [t] : [];
  }

  /**
   * Penge i kassen og en by i mørket: sig det højt én gang pr. mørk periode. Playtest viste spillere,
   * der tabte med 60-90 kr ubrugt, fordi de ikke så, at pengene kunne redde byen.
   */
  function moneyNudge(): void {
    const s = game.state;
    // Kan denne skærm ikke bygge (Driftsvagten), er pengene ikke hendes at bruge: hintet peger på
    // makkeren i stedet (co-op runde 2).
    if (screens.current || game.over || game.brain || game.theirs('build_line')) return;
    for (const n of s.nodes) {
      if (!n.city || !n.active || n.phantom) continue;
      if (n.city.clock <= 0 && !n.city.out) {
        nudged.delete(n.id);
        continue;
      }
      // Uret i uretick (100 pr. tick ved fuld mangel): cirka 3 s mørke, før vi siger noget.
      if (nudged.has(n.id) || s.budget < 40 || (n.city.clock < 3000 && !n.city.out)) continue;
      nudged.add(n.id);
      const act = advice && !advice.partner ? hintAction(advice) : null;
      // Sig hvad pengene skal bruges til: det samme råd som hintet, ikke bare "brug dem".
      // Ringen (Systemansvarlig) dikterer aldrig en rute: dér peger vi kun på byen (playtest runde 3).
      const doable = advice && !advice.partner && advice.display !== 'ring' && advice.kind !== 'praise' && advice.kind !== 'calm' && advice.kind !== 'save';
      const how = advice && doable ? adviceText(s, advice, touch) : `Tryk på ${n.name} og se, hvad den mangler.`;
      // Intet beløb: kassen ændrer sig (Volt bruger også af den), og toasten må aldrig stå med et gammelt tal.
      toast.show(`${s.coop ? 'I' : 'Du'} har penge i kassen. ${how}`, 'warn', 4200, sc.nodeScreen(n.id), act ? { label: 'Vis mig', run: act } : null);
      return;
    }
  }

  // Lærling: en ny by eller et nyt varsel pauser spillet et øjeblik (højst 5 s), så man kan nå at se
  // det. Pausen slutter, når spilleren gør noget eller trykker på afspil.
  let pauseWhy = '';
  let pauseEnd = 0;
  let pauseActs = 0;

  /** Sand, hvis spillet faktisk holdt pause. */
  function eventPause(why: string): boolean {
    if (game.brain || attract || online.inGame || !game.running || screens.current) return false;
    if (difficulty(game.state.difficulty).autopause !== 'yes') return false;
    game.paused = true;
    pauseWhy = why;
    pauseEnd = performance.now() + 5000;
    pauseActs = game.acts;
    return true;
  }

  function resumeEventPause(): void {
    // Mens en skærm (fx årsskiftet) står fremme, venter pausen; ellers bliver den hængende for evigt.
    if (!pauseWhy || screens.current) return;
    pauseWhy = '';
    game.paused = false;
  }

  // IGN-løkke runde 2: "Lærling kan tabes (hjerter uden autopause)". Lærling holder pause 5 s før
  // en by går i sort og peger på den, én gang pr. mørkeur.
  const heartWarned = new Set<string>();
  const capital = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);
  function heartWatch(): void {
    const s = game.state;
    if (game.paused || s.phase !== 'playing' || difficulty(s.difficulty).autopause !== 'yes') return;
    for (const n of s.nodes) {
      if (!n.city) continue;
      const left = darkInTicks(s, game.level, n);
      if (left === null || left > 5 * TICK_HZ) {
        if (left === null) heartWarned.delete(n.id);
        continue;
      }
      const a = cityAdvice(s, n.id);
      if (heartWarned.has(n.id) || (a?.kind === 'calm' && a.soon === true)) continue;
      heartWarned.add(n.id);
      // Spor "sandhed": sig flaskehalsen præcist ("ledningen Skive-Viborg er fuld").
      const b = bottleneck(s, n.id);
      const cause = b.kind === 'ok' ? '' : ` ${capital(bottleneckText(b))}.`;
      if (eventPause(`${n.name} går i sort om 5 s.`)) {
        toast.show(`${n.name} går i sort om 5 s og koster et hjerte.${cause} Tryk på byen og se, hvad der hjælper.`, 'warn', 4200, sc.nodeScreen(n.id));
      }
      return;
    }
  }

  /** Årsskiftet og andre skærme tager over: en hændelsespause må ikke overleve dem. */
  function dropEventPause(): void {
    if (!pauseWhy) return;
    pauseWhy = '';
    game.paused = false;
  }

  /** Et tryk på hintet gør det, hintet siger: åbner menuen for ledningen eller stationen. */
  function hintAction(a: Advice): (() => void) | null {
    switch (a.kind) {
      case 'reinforce':
        return a.line === REINFORCE_ALL ? () => controls.act('reinforce_mast', REINFORCE_ALL) : () => controls.openLine(a.line);
      case 'upgrade':
        return () => controls.openLine(a.line);
      case 'save':
        return a.line ? () => controls.openLine(a.line as string) : null;
      case 'station':
      case 'battery':
      case 'flywheel':
        return () => controls.openNode(a.node);
      case 'hire':
        return () => controls.act('hire_crew');
      default:
        return null;
    }
  }

  // Onboarding: hånden viser første træk, første opgradering og første forstærkning mod storm med
  // det samme, uanset sværhed, og siger kun ét ord. Lektionen er klaret, når spilleren selv gør det.
  // Fra M2 også første svar på et hændelseskort og første power-up.
  const LESSON_OF: Partial<Record<string, Lesson>> = {
    build_line: 'drag',
    upgrade_line: 'upgrade',
    reinforce_mast: 'reinforce',
    answer_card: 'card',
    power_fcr: 'power',
    power_shed: 'power',
    power_norway: 'power',
  };
  /** Hvor hånden sidst pegede (skærm), så jublen lander der, også når den pegede på en knap. */
  let guideAt: P | null = null;

  function lessonAct(kind: string): void {
    const l = LESSON_OF[kind];
    if (!l || lessonDone(l) || game.brain) return;
    markLesson(l);
    sfx.play('lesson');
    // Jubel lige der, hvor hånden pegede.
    const at =
      l === 'card' || l === 'power' ? guideAt : l === 'drag' && drag ? sc.nodeScreen(drag.to) : advice && 'line' in advice && advice.line ? sc.lineScreen(advice.line) : null;
    guide.cheer(at, ICON.star);
  }

  function lessonFor(a: Advice): Lesson | null {
    if (a.kind === 'build') return 'drag';
    if (a.kind === 'upgrade') return 'upgrade';
    if (a.kind === 'reinforce') return 'reinforce';
    if (a.kind === 'card') return 'card';
    if (a.kind === 'power') return 'power';
    return null;
  }

  /** Skærmpunkterne for byer med mørkeur (eller allerede i sort). */
  function darkCities(): P[] {
    const out: P[] = [];
    for (const n of game.state.nodes) {
      if (!n.city || !n.active || n.phantom || (n.city.clock <= 0 && !n.city.out)) continue;
      const p = sc.nodeScreen(n.id);
      if (p) out.push(p);
    }
    return out;
  }

  /**
   * Ringmenuens frie felt: under HUD'en (og pauseknappen), over eller til venstre for værktøjerne og
   * power-ups, med byer med mørkeur som punkter, menuen helst ikke dækker.
   */
  function ringArea(): RingArea {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const m = 8;
    const hr = hud.el.getBoundingClientRect();
    let right = W - m;
    let bottom = H - m;
    // HUD'en og nødknapperne i venstre søjle (telefon på siden) lukker fra venstre, ellers fra oven.
    const side = root.classList.contains('side');
    for (const el of side ? [bar.el] : [bar.el, powers.el]) {
      if (el.hidden) continue;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      // Står den i bunden (højformat), lukker den nedefra; ellers står den i højre side (skinne).
      if (r.top > H / 2 && r.width > W / 2) bottom = Math.min(bottom, r.top - m);
      else if (r.left > W / 2) right = Math.min(right, r.left - m);
      else if (r.top > H / 2) bottom = Math.min(bottom, r.top - m);
    }
    const sr = stage.getBoundingClientRect();
    const avoid = darkCities().map((p) => ({ x: p.x + sr.left, y: p.y + sr.top }));
    // IGN-løkke 2: ringen lagde sig oven på nabobyen, og næste træk startede på en knap i stedet for
    // byen. Alle byer og værker er nu punkter, ringen helst ikke dækker (billigere end en mørk by).
    const nodes: P[] = [];
    for (const n of game.state.nodes) {
      if (!n.active || n.phantom || n.kind === 'foreign') continue;
      const p = sc.nodeScreen(n.id);
      if (p) nodes.push({ x: p.x + sr.left, y: p.y + sr.top });
    }
    const cr = cards.isOpen ? cards.el.getBoundingClientRect() : duo.isOpen ? duo.el.getBoundingClientRect() : null;
    const block = cr && cr.width > 0 ? { left: cr.left - m, top: cr.top - m, right: cr.right + m, bottom: cr.bottom + m } : undefined;
    return { left: side ? hr.right + m : m, top: side ? m : Math.max(m, hr.bottom + m), right, bottom, avoid, nodes, block };
  }


  /** Hånden: med det samme for det vigtige på Lærling, for alt akut og for lektioner, ellers når samme råd har stået ubrugt i 4 s. */
  function guideShow(now: number): GuideShow {
    cardTip = null;
    const g = guideWhere(now);
    guideAt = g ? (g.mode === 'tap' ? g.at : g.to) : null;
    const rk = g && g.mode === 'tap' && g.ring ? `ring:${adviceKey}` : '';
    if (rk !== ringLast) {
      ringLast = rk;
      if (rk) hintLog.push({ tick: game.state.tick, year: game.state.year, text: rk, urgent: false, ring: true });
    }
    cards.tip(cardTip);
    return g;
  }

  /** Svaret, træneren foreslår på hændelseskortet i denne frame: knappen lyser op (ingen hånd over den). */
  let cardTip: CardOption | null = null;

  function guideWhere(now: number): GuideShow {
    const a = advice;
    if (!a || screens.current || game.over || game.brain || ring.isOpen || sc.selected || controls.dragging) return null;
    const laerling = game.state.difficulty === 'laerling';
    if (a.kind === 'praise' || a.kind === 'save' || a.kind === 'calm') return null;
    // Makkerens job: ingen hånd, kun hintet med ping (IGN-løkke runde 4).
    if (a.partner) return null;
    if (a.display === 'ring') return ringWhere(a);
    const lesson = lessonFor(a);
    const learning = lesson !== null && !lessonDone(lesson);
    // Kort og power-ups: hånden peger på knappen, første gang med "Tryk!". Bagefter kun på Lærling eller
    // når rådet har stået ubrugt et stykke tid; kortet er stort nok i sig selv.
    if (a.kind === 'card' || a.kind === 'power') {
      // Kortet: ingen hånd (Spor: kortet: hånden dækkede knappen). Det foreslåede svar lyser op på
      // selve kortet, første gang og altid på Lærling.
      if (a.kind === 'card') {
        if (learning || laerling) cardTip = a.opt;
        return null;
      }
      if (!learning && !laerling && now - adviceSince < 4000) return null;
      const word = learning ? 'Tryk!' : undefined;
      const at = a.power === 'shed' && a.target && powers.isPicking ? powers.pickCenter(a.target) : powers.center(a.power);
      return at ? { mode: 'tap', at, word, over: true } : null;
    }
    if (bar.tool !== 'build') return null;
    const teach = a.urgent || a.kind === 'build' || a.kind === 'upgrade' || a.kind === 'station' || a.kind === 'battery';
    if (!learning && !(teach && (laerling || a.urgent)) && now - adviceSince < 4000) return null;
    const word = learning ? (lesson === 'drag' ? 'Træk!' : 'Tryk!') : undefined;
    if (drag) {
      const from = sc.nodeScreen(drag.from);
      const to = sc.nodeScreen(drag.to);
      return from && to ? { mode: 'drag', from, to, word } : null;
    }
    if (a.kind === 'shoo') {
      const at = sc.threatScreen(a.threat);
      return at ? { mode: 'tap', at } : null;
    }
    if (a.kind === 'reinforce' && a.line === REINFORCE_ALL) {
      const at = bar.stormCenter();
      return at ? { mode: 'tap', at, word, over: true } : null;
    }
    if (a.kind === 'reinforce' || a.kind === 'upgrade') {
      const at = sc.handPoint(a.line, a.kind === 'reinforce');
      return at ? { mode: 'tap', at, word } : null;
    }
    if (a.kind === 'station' || a.kind === 'battery' || a.kind === 'flywheel') {
      const at = sc.nodeScreen(a.node);
      return at ? { mode: 'tap', at } : null;
    }
    return null;
  }

  /** Kun en pulserende turkis ring om noden, ledningen eller gæsten, rådet handler om. */
  function ringWhere(a: Advice): GuideShow {
    let at: { x: number; y: number } | null = null;
    if (a.kind === 'reinforce' && a.line === REINFORCE_ALL) {
      const b = bar.stormCenter();
      return b ? { mode: 'tap', at: b, ring: true, over: true } : null;
    }
    if (a.kind === 'build' || a.kind === 'station' || a.kind === 'battery' || a.kind === 'flywheel') at = sc.nodeScreen(a.node);
    else if (a.kind === 'shoo') at = sc.threatScreen(a.threat);
    else if (a.kind === 'reinforce' || a.kind === 'upgrade' || a.kind === 'broken' || a.kind === 'hire') at = sc.handPoint(a.line, a.kind === 'reinforce');
    return at ? { mode: 'tap', at, ring: true } : null;
  }

  // -------------------------------------------------------------------------
  // Layout: frit felt mellem HUD og værktøjer, som kameraet fitter kortet i.

  /** Om kortet sidst blev lagt ud til titlen (banen i det frie felt ved siden af titelkortet). */
  let layoutTitle = false;
  let barEdge = { key: '', left: 0, top: 0, hud: 0 };
  function layout(): void {
    const W = window.innerWidth;
    const H = window.innerHeight;
    const rail = W > H || W >= 900;
    // Lavt bredformat (telefon på siden): HUD, knapper og nødknapper står i en smal venstre søjle,
    // så kortet får hele højden mellem søjlen og skinnen (IGN-løkke 2, runde 2: kortet fik 55 % af
    // bredden og noderne stod 30-45 px fra hinanden).
    const side = rail && H <= 560;
    root.classList.toggle('rail', rail);
    root.classList.toggle('side', side);
    root.classList.toggle('portrait', !rail);
    app.resize();
    powers.place(rail, bar.el);
    // Power-ups tager plads fra kortet: en række øverst i værktøjslinjen, eller bunden af skinnen.
    const pr = powers.el.hidden ? null : powers.el.getBoundingClientRect();
    const powersH = pr && pr.height > 0 ? Math.ceil(pr.height) + 6 : 0;
    root.style.setProperty('--powers-h', `${powersH}px`);
    const hr = hud.el.getBoundingClientRect();
    const tr0 = bar.el.getBoundingClientRect();
    // IGN-løkke runde 4: værtens kamera pendlede 35-40 px efter hver handling, fordi værktøjslinjen
    // blev en anelse bredere eller højere (Fortryd, et badge, en pris på to linjer). Kortets felt
    // følger nu linjens største udstrækning i dette format og krymper højst én gang.
    const key = `${W}x${H}|${rail}|${game.level.id}`;
    if (barEdge.key !== key) barEdge = { key, left: W, top: H, hud: hr.bottom };
    barEdge.hud = Math.max(barEdge.hud, hr.bottom);
    if (tr0.width > 0) {
      barEdge.left = Math.min(barEdge.left, tr0.left);
      barEdge.top = Math.min(barEdge.top, tr0.top);
    }
    const tr = { left: barEdge.left, top: barEdge.top, height: tr0.height };
    // Hint, toast og Volt står over værktøjslinjen: dens målte højde, ikke et gæt (på 320 px
    // brydes prisen, linjen bliver højere, og hintet lagde sig oven på den).
    if (!rail && tr.height > 0) root.style.setProperty('--bar-clear', `${Math.round(H - tr.top + 8)}px`);
    else root.style.removeProperty('--bar-clear');
    placeMessages();
    const pad = 10;
    // Under HUD'en, eller helt oppe, når HUD'en står i venstre søjle.
    const top = side ? hr.top : hr.bottom + pad;
    // Kortet starter under vejrudsigtens strimmel: playtest runde 3 fik en ny by med ur og navn gemt
    // bag "Uvejr nu"-chippen. Spor: kortet: strimlens og hintets højde måles fra deres CSS (også når
    // de er skjult), ikke gættes, så feltet er det, spilleren faktisk ser.
    const mapTop = (side ? hr.top - 4 : barEdge.hud) + 4 + cssHeight(fc.el, 30) + 4;
    const left = side ? Math.ceil(hr.right) + pad : pad;
    // Hintet står nederst over kortet (én linje); i lavt bredformat står det oven på kortets bund,
    // så kortet får det meste af højden.
    const hintH = rail && H < 500 ? 8 : hintLine() + 6;
    let safe = rail
      ? { x: left, y: mapTop, w: Math.max(100, tr.left - pad - left), h: Math.max(100, H - mapTop - pad - hintH) }
      : { x: pad, y: mapTop, w: W - pad * 2, h: Math.max(100, tr.top - mapTop - pad - hintH - (powers.docked ? 0 : powersH)) };
    // Telefon på siden: knapperne står til venstre for skinnen, så kortet holder sig fri af dem.
    if (rail && !side && pr && powersH > 0 && pr.right < tr.left) safe.w = Math.max(100, pr.left - pad * 2);
    // Bag titlen spiller banen i det frie felt ved siden af (bredformat) eller over kortet (højformat).
    const tc = screens.current === 'title' ? screens.el.querySelector<HTMLElement>('.title-card') : null;
    if (tc) {
      const sat = 12;
      safe = rail
        ? { x: tc.offsetLeft + tc.offsetWidth + pad, y: sat, w: Math.max(100, W - tc.offsetLeft - tc.offsetWidth - pad * 2), h: Math.max(100, H - sat - pad) }
        : { x: pad, y: sat, w: W - pad * 2, h: Math.max(100, tc.offsetTop - sat - pad) };
    }
    layoutTitle = !!tc;
    // Volts panel tager venstre side (stor skærm) eller lidt af bunden (telefon).
    const vi = tc ? null : volt.inset(rail);
    if (vi) safe = { x: safe.x + vi.left, y: safe.y, w: Math.max(100, safe.w - vi.left), h: Math.max(100, safe.h - vi.bottom) };
    sc.layout(safe);
    // Hændelseskortet står fast i bunden (CSS); det må højst nå op til lige under HUD'en.
    cards.place(H - top - pad);
    duo.place(H - top - pad);
    // Vejrudsigten står lige under HUD'en, midt over kortets felt, i luften over husene (feltets top
    // har altid mindst 40 px luft). Knappen hjem står under den, så de aldrig dækker hinanden.
    root.style.setProperty('--fc-top', `${Math.round(side ? hr.top : hr.bottom + 4)}px`);
    root.style.setProperty('--fc-x', `${Math.round(tc ? W / 2 : safe.x + safe.w / 2)}px`);
    // Hint og toast står midt under kortets felt (venstre søjle eller ej).
    root.style.setProperty('--map-cx', `${Math.round(safe.x + safe.w / 2)}px`);
    root.style.setProperty('--map-w', `${Math.round(Math.max(160, safe.w - 16))}px`);
    recenterBtn.style.left = `${Math.round(safe.x + 4)}px`;
    recenterBtn.style.top = `${Math.round(side ? safe.y + 4 : Math.max(safe.y + 4, hr.bottom + 4 + 36 + 6))}px`;
    findBtn.style.left = recenterBtn.style.left;
    findBtn.style.top = recenterBtn.style.top;
  }
  /** Et elements højde fra dets CSS: virker også, mens det er skjult (display: none). */
  function cssHeight(el: HTMLElement, fallback: number): number {
    const v = parseFloat(getComputedStyle(el).height);
    return Number.isFinite(v) && v > 0 ? v : fallback;
  }

  /** Hintets højde med én linje tekst, fra dets CSS (polstring plus linjehøjde). */
  function hintLine(): number {
    const cs = getComputedStyle(toast.hint);
    const fs = parseFloat(cs.fontSize) || 16;
    const lh = parseFloat(cs.lineHeight);
    const v = (Number.isFinite(lh) ? lh : fs * 1.25) + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    return Math.round(Math.max(24, Math.min(64, v)));
  }

  /** Volts dok står over hintet: et hint på tre linjer (lange stednavne) skubber den op i stedet for
   *  at lægge sig under den. Kun højden måles; kortet flytter sig ikke. */
  function placeMessages(): void {
    let h = 0;
    for (const el of [toast.hint, toast.el]) {
      if (el.hidden || el.classList.contains('top')) continue;
      h = Math.max(h, el.offsetHeight);
    }
    root.style.setProperty('--msg-h', `${Math.max(54, Math.ceil(h))}px`);
  }

  /** Tankebobler, vejrbrikker og Volts hjørne i skærmkoordinater (kortets lærred starter i 0,0). */
  function overlayRects(): Array<{ x: number; y: number; w: number; h: number }> {
    const out = [...juice.rects(), ...bubbleRects()];
    // Hintet står længe, så bynavne viger for det; toasten er kort og må dække et øjeblik.
    const els = [...(fc.el.hidden ? [] : Array.from(fc.el.children)), ...Array.from(root.querySelectorAll('.volt:not([hidden]) :is(.volt-portrait, .volt-src, .volt-toggle, .volt-say)')), ...Array.from(root.querySelectorAll('.powers:not([hidden]) .power:not([hidden])'))];
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.width > 0) out.push({ x: r.left, y: r.top, w: r.width, h: r.height });
    }
    return [...out, ...hintRects()];
  }

  /** Hintet, når det står fremme: navne viger for det med det samme, ikke først efter 0,4 s (labels.ts: firm). */
  function hintRects(): Array<{ x: number; y: number; w: number; h: number }> {
    if (toast.hint.hidden || toast.hint.classList.contains('under')) return [];
    const r = toast.hint.getBoundingClientRect();
    return r.width > 0 ? [{ x: r.left, y: r.top, w: r.width, h: r.height }] : [];
  }

  /** Guidens "Træk!"-boble og prisboblen under en ledning, som tankebobler og bynavne viger for. */
  function bubbleRects(): Array<{ x: number; y: number; w: number; h: number }> {
    const out: Array<{ x: number; y: number; w: number; h: number }> = [];
    const word = guide.el.hidden ? null : guide.el.querySelector<HTMLElement>('.guide-word:not([hidden])');
    for (const el of [word, bubble.hidden ? null : bubble, nodeCard.el.hidden ? null : nodeCard.el]) {
      const r = el?.getBoundingClientRect();
      if (r && r.width > 0) out.push({ x: r.left, y: r.top, w: r.width, h: r.height });
    }
    return out;
  }
  window.addEventListener('resize', layout);
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => layout());
    ro.observe(hud.el);
    const mo = new ResizeObserver(() => placeMessages());
    mo.observe(toast.hint);
    mo.observe(toast.el);
    // Power-ups dukker op eller forsvinder med banen; så skifter kortets frie felt.
    ro.observe(powers.el);
  }

  // -------------------------------------------------------------------------
  // Fokus: pause når fanen skjules eller vinduet mister fokus.

  const autoPause = (): void => {
    // Sammen styrer rummet pausen; en skjult fane pauser ikke makkeren.
    if (online.inGame) return;
    if ((game.running || pauseWhy || game.cardHold) && (!game.brain || pilot) && !game.over) {
      game.paused = true;
      pauseWhy = '';
    }
  };
  window.addEventListener('blur', autoPause);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) autoPause();
  });

  // -------------------------------------------------------------------------
  // Hovedløkken

  const covered0 = (): boolean => screens.current !== null;

  /**
   * Auto-spol kun solo, på selve banen: aldrig i et rum, på titlen, resultat- eller årsskifteskærmen,
   * og ikke mens bane 1 lærer spilleren at trække (til første ledning og øvelsesstormen er klaret).
   */
  function spolAllowed(): boolean {
    if (online.inGame || game.remote || attract || screens.current !== null || game.over) return false;
    if (game.state.phase !== 'playing') return false;
    // AI'en spiller i et tempo, man kan følge: ingen ×3.
    if (pilot) return false;
    if (game.brain) return true;
    if (!lessonDone('drag')) return false;
    if (tutorialStorm(game.state)) return false;
    const a = advice;
    const l = a ? lessonFor(a) : null;
    return !(l && !lessonDone(l));
  }
  // -------------------------------------------------------------------------
  // Lad AI spille (src/ai/pilot.ts): ekspert-autoplayen tager spillerens plads i et tempo, man kan følge.
  // Hvert træk meldes i bjælken og med hånden på kortet, før det sker. Et tryk på Tag over, A eller
  // et træk, spilleren selv gør, giver roret tilbage. En bane, AI'en har spillet, giver ingen stjerner.

  let pilot: Pilot | null = null;
  /** Spillerens egne handlinger, da AI'en tog over: ændrer tallet sig, har spilleren taget over. */
  let pilotActs = 0;
  /** AI'en har spillet på denne bane (ingen stjerner, hvis den vindes). */
  let pilotUsed = false;

  /** Kun solo på en bane i gang: aldrig i et rum, bag titlen, med Volt eller ?autoplay. */
  function pilotCan(): boolean {
    return started && !attract && !online.inGame && !game.remote && !volt.on && !game.over && (!game.brain || !!pilot);
  }

  /**
   * Tips-turen kommer kun første gang og aldrig i et rum (der står tiden ikke stille for én).
   * ?tips=1 tvinger den frem og ?tips=0 slår den fra. Under automatiseret test (webdriver) er den
   * fra, medmindre ?tips=1 står der, så de øvrige playtests ikke skal klikke den væk.
   */
  const tipsParam = new URLSearchParams(location.search).get('tips');
  function tipsWanted(): boolean {
    if (online.inGame || game.remote || attract || tipsParam === '0') return false;
    if (tipsParam !== '1' && navigator.webdriver) return false;
    return !load().tips;
  }

  function openTips(): void {
    tipsDue = 0;
    if (tips.open || online.inGame || game.over) return;
    stopPilot(true);
    ring.close();
    controls.select(null);
    nodeCard.hide();
    toast.clear();
    game.held = true;
    tips.start();
  }

  function startPilot(): void {
    if (pilot || !pilotCan()) return;
    pilot = new Pilot(game.state.seed + game.state.tick, game.player);
    pilotUsed = true;
    pilotActs = game.acts;
    game.brain = pilot.brain;
    ring.close();
    controls.select(null);
    nodeCard.hide();
    // En verserende årsskiftepause slippes: AI'en vælger selv gaven efter et øjeblik.
    if (game.state.phase === 'yearend' && !online.inGame) game.held = false;
    if (game.paused && screens.current === null) game.paused = false;
    // Ingen toast: bjælken siger selv, at AI'en spiller, og en toast lagde sig oven på den (telefon).
    toast.clear();
  }

  function stopPilot(quiet = false): void {
    if (!pilot) return;
    if (game.brain === pilot.brain) game.brain = null;
    pilot = null;
    if (!quiet && !game.over) toast.say('Du har roret igen.', 'good', 1600);
  }

  /** AI'ens næste træk som hånd på kortet: træk mellem byerne eller tryk på målet. */
  function pilotGuide(): GuideShow {
    const m = pilot?.next;
    if (!m || screens.current || game.over) return null;
    const { kind, target } = m.intent;
    if (kind === 'build_line' && target) {
      const [a, b] = target.split('|');
      const from = a ? sc.nodeScreen(a) : null;
      const to = b ? sc.nodeScreen(b) : null;
      return from && to ? { mode: 'drag', from, to } : null;
    }
    let at: { x: number; y: number } | null = null;
    if (kind === 'reinforce_mast' && target === REINFORCE_ALL) at = bar.stormCenter();
    else if (target && (kind === 'upgrade_line' || kind === 'bury_line' || kind === 'reinforce_mast' || kind === 'dispatch_crew')) at = sc.handPoint(target, kind === 'reinforce_mast');
    else if (target && (kind === 'upgrade_station' || kind === 'build_battery' || kind === 'build_flywheel')) at = sc.nodeScreen(target);
    else if (target && kind === 'shoo_threat') at = sc.threatScreen(target);
    else if (kind === 'power_fcr' || kind === 'power_norway' || kind === 'power_shed') at = powers.center(kind.slice(6) as 'fcr' | 'norway' | 'shed');
    return at ? { mode: 'tap', at } : null;
  }

  // A: Lad AI spille / Tag over (ikke i tekstfelter, ikke med modtaster).
  window.addEventListener('keydown', (e) => {
    if (e.code !== 'KeyA' || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
    if (pilot) stopPilot();
    else if (pilotCan() && screens.current === null) startPilot();
    else return;
    e.preventDefault();
  });

  let prev = performance.now();
  const frame = (now: number): void => {
    requestAnimationFrame(frame);
    // Simulationen følger uret op til 250 ms pr. billede, så en langsom telefon ikke spiller i slowmotion;
    // animationerne får højst 100 ms, så de ikke springer.
    const raw = Math.min(250, Math.max(0, now - prev));
    const dt = Math.min(100, raw);
    prev = now;
    if (document.hidden) return;
    fps = fps * 0.95 + (1000 / Math.max(1, raw)) * 0.05;
    // Hit-stop: ved udfald og reddede ledninger fryser verden et splitsekund.
    // Aldrig hit-stop i makkerrum: rummet ejer uret.
    sc.freezeOk = !online.inGame && !game.remote;
    hud.calm = calmNow();
    const ts = sc.timeScale(now);
    online.frame(now, dt);
    game.spol.allowed = spolAllowed();
    // Planen fra pausen før uret tæller: ellers kan et tick (en storm, en gæst) nå at ændre nettet
    // mellem forhåndsvisningen og byggeriet.
    controls.flushPlanned();
    const alpha = game.advance(raw * ts);
    // ×3-pillen forklarede sig kun i en hover-titel, som ikke findes på touch. Første gang siger vi det.
    if (!spolTold && game.spol.active && game.state.phase === 'playing') {
      spolTold = true;
      toast.show('Stille lige nu, så uret løber ×3. Det bremser selv, når der sker noget.', 'info', 3200);
    }
    const wdt = dt * ts;
    // Ét fokuslag ad gangen (focus.ts). Spillerens egne menuer går foran hændelseskortet, som venter
    // usynligt bagved; åbner spilleren en ny menu, lukker den gamle.
    const f = focus.update({
      gave: screens.current === 'yearend',
      ping: online.coop.wheelOpen,
      ring: ring.isOpen,
      bykort: !!nodeCard.shown,
      kort: (cards.pending || duo.pending(game)) && screens.current === null && !game.over,
    });
    for (const l of f.close) {
      if (l === 'ring') ring.close();
      else if (l === 'bykort') nodeCard.hide();
      else if (l === 'ping') online.coop.closeWheel();
    }
    // IGN-løkke runde 2: kameraet står stille, mens en finger er nede, og mens en menu, et kort eller
    // et bykort er åbent; ellers gled målet væk under tommelfingeren (Spor: kortet: også kortet).
    // Spor: telefonkort: enhver finger på kortet fryser et glid (også et tryk på tom grund), og når
    // den slipper, venter kameraet (releasedAt) som efter et træk.
    sc.holdFrame = controls.touching || (f.top !== null && f.top !== 'gave');
    sc.fingerDown = controls.touching;
    toast.prune();
    sc.holdPaused = game.paused && !game.softPause && !online.inGame;
    if (pendingYear && !controls.holding && game.state.phase === 'yearend') {
      const p = pendingYear;
      pendingYear = null;
      showYearEnd(p.s, p.year, p.stats);
    } else if (pendingYear && game.state.phase !== 'yearend') {
      // Årsskiftet er overstået uden et valg (fx online, hvor makkeren valgte): slip ventetiden igen,
      // ellers står solo stille for altid.
      pendingYear = null;
      if (!online.inGame && !game.remote && !game.brain) game.held = false;
      controls.flushAfterYear();
    }
    // Faste DOM-flader over kortet fra sidste frame: et navn lagde sig under "Hallo?",
    // vejrbrikken og Volts hjørne (break-ui). Toasten tæller ikke med; den er væk om lidt,
    // og navne må ikke hoppe for den.
    sc.overlays = overlayRects();
    sc.firm = hintRects();
    sc.frame(game.state, now, wdt, alpha);
    sc.render();
    const cur = screens.current;
    if ((cur === 'title') !== layoutTitle) layout();
    audio.setMode(game.over && !attract ? 'off' : cur === null && !attract ? 'play' : 'calm');
    audio.frame(game.state, dt, game.running && cur === null && !attract);
    heartWatch();
    game.softPause = !!pauseWhy && game.paused;
    toast.freeze(game.paused && !game.over);
    // Spilleren gjorde selv noget: så har spilleren taget over, og AI'en slipper roret.
    if (pilot && game.acts !== pilotActs) stopPilot();
    hud.aiCan = pilotCan();
    hud.aiOn = !!pilot;
    hud.update(game, muted);
    pilotBar.update(pilot, covered0() || game.over);
    if (tipsDue && now >= tipsDue && !covered0() && !controls.dragging && game.running && game.state.phase === 'playing') openTips();
    if (tips.open) game.held = true;
    tips.update();
    // Teksten lover, at årets byer er klaret. Derfor kræver den, at hver by faktisk har strøm nok
    // og et tomt mørkeur (IGN runde 2: den stod, mens Struer var nede på 20 %), og den forsvinder,
    // så snart det ikke længere er sandt.
    const allLit = (): boolean => hud.callShown && game.state.nodes.every((n) => !n.city || !n.active || n.phantom || (n.city.clock === 0 && isPowered(n)));
    if (!callExplained && !covered0() && !game.brain && allLit()) {
      callExplained = true;
      toast.show(`Årets byer og uvejr er klaret. Start næste år nu, og få ${earlyBonusPct(game.state)} % mere ved årsskiftet.`, 'good', 4200, null, null, allLit);
    }
    bar.update(game, game.level.mechanics);
    const covered = screens.current !== null;
    // IGN-løkke 2, runde 3: kortet venter aldrig usynligt. Står spillerens egen menu foran det, står
    // kortet som en fane ("Svar på kortet"); kun en skærm eller banens slutning skjuler det.
    // Det fælles lyn går foran et hændelseskort: kortet venter, så de aldrig står oven i hinanden.
    const behind = covered || game.over || (f.top !== null && f.top !== 'kort');
    duo.update(game, behind);
    cards.peek = focus.peeking;
    cards.partner = online.inGame ? 'Driftsvagten' : 'Volt';
    cards.update(game, covered || game.over || duo.pending(game), controls.holding);
    // Solo står uret stille, mens kortet venter på svar: ingen nedtælling at hidse sig op over, og intet
    // sker bag kortet. Aldrig i et rum (rummet ejer uret), med autoplay eller bag titlen.
    game.cardHold = cards.pending && !covered && !game.over && !game.brain && !game.remote && !online.inGame && !attract && game.state.phase === 'playing';
    volt.frame(now);
    fc.update(game.state, !covered && !game.over && !attract);
    juice.frame(now, !covered && !attract && !game.over, waitingCustomers(game.state), (id) => sc.nodeScreen(id), sc.k, bubbleRects());
    powers.update(game, game.level.mechanics, covered || game.over);
    // Bylisten til Nødaflastning ligger nederst; toasten må ikke dække den.
    toast.setTop(powers.isPicking);
    root.classList.toggle('covered', covered);
    root.classList.toggle('carding', cards.isOpen || duo.isOpen);
    root.classList.toggle('paused', game.paused && !covered && !game.over);
    sc.planned(controls.plannedPairs);
    const off = covered || game.over || attract ? null : sc.offscreen;
    findBtn.hidden = !off;
    if (off) {
      findArrow.style.transform = `rotate(${Math.round((Math.atan2(off.dy, off.dx) * 180) / Math.PI)}deg)`;
      const name = nodeById(game.state, off.id)?.name ?? 'byen';
      if (findBtn.dataset.id !== off.id) {
        findBtn.dataset.id = off.id;
        findBtn.setAttribute('aria-label', `Find ${name}: uret løber`);
      }
    }
    recenterBtn.hidden = covered || sc.atHome || !!off;
    // Bykortet hører til den valgte by: et nyt tryk, et træk eller en skærm foran lukker det.
    if (nodeCard.shown && (sc.selected !== nodeCard.shown || covered || game.over || controls.holding)) nodeCard.hide();
    nodeCard.update(game.state, game.level, sc.nodeScreen(nodeCard.shown ?? ''), sc.k, sc.mapArea, touch, {
      player: game.player,
      partner: online.inGame ? (game.player === 'p1' ? 'Driftsvagten' : 'Planlæggeren') : 'Volt',
    });
    if (yearView && game.state.phase === 'yearend') yearView.update(game.state);
    updateHint(now);
    // Én besked ad gangen (messages.ts): udfald > kort > svar > guide > Volt > toast.
    const gs = pilot ? pilotGuide() : guideShow(now);
    const tc = toastChannel(toast.wantPrio);
    const want: Partial<Record<Channel, boolean>> = { kort: cards.isOpen || duo.isOpen, guide: gs !== null, volt: volt.panel.speaking };
    if (tc) want[tc] = true;
    const pick = arbitrate(want);
    // Et udfald går kun foran kortet, når uret går (makkerrum): solo står alt stille bag kortet, og
    // udfaldet venter i køen, til kortet er besvaret.
    const udfaldFirst = pick === 'udfald' && f.top === 'kort' && !game.cardHold;
    const quiet = focus.quiet && !udfaldFirst;
    root.classList.toggle('focus-quiet', quiet);
    // IGN-løkke 2: årsskiftet og resultatet er spillets pauseskærme. Et udfald fra sidste tick ("Lemvig
    // gik i sort.") kom ellers frem oven på resultatet; det venter i køen, og skærmskiftet rydder den.
    const interrupt = screens.current === 'yearend' || screens.current === 'result';
    toast.hold(interrupt || quiet || (pick !== null && pick !== tc));
    cards.hold(udfaldFirst);
    duo.hold(udfaldFirst);
    volt.panel.hold(quiet || pick !== 'volt');
    const gShow = pick === 'guide' && !quiet ? gs : null;
    guide.update(gShow, gShow && !gShow.over && !gShow.ring ? sc.labelRects() : []);
    quality.sample(dt);
  };

  // -------------------------------------------------------------------------
  // Start

  newGame(levelId, params.seed);
  if (params.direct) {
    if (autoplay) {
      game.brain = autoplayBrain(autoplay, params.seed);
      play();
    } else {
      showLevelStart(levelId);
    }
  } else {
    // Titlen først, med en levende bane bagved. Spil fører første gang direkte til bane 1.
    showTitle();
    // Et makkerlink (/r/KODE eller ?rum=KODE) går direkte ind i rummet.
    const roomCode = codeFromUrl(location.pathname, location.search);
    if (roomCode) online.open(roomCode);
    else if (/^\/r\/[^/]+\/?$/.test(location.pathname) || new URLSearchParams(location.search).has('rum')) online.openBadLink();
  }
  layout();
  root.classList.add('ready');
  requestAnimationFrame(frame);

  // -------------------------------------------------------------------------
  // Testkrog

  const page = (p: { x: number; y: number } | null): { x: number; y: number } | null => {
    if (!p) return null;
    const r = stage.getBoundingClientRect();
    return { x: Math.round(p.x + r.left), y: Math.round(p.y + r.top) };
  };
  (window as unknown as { __net: unknown }).__net = {
    get info() {
      return { ...online.debug(), hash: hash(game.state) };
    },
    advice: () => online.adviceIntent(),
    sync: () => online.sync(),
    pause: (on: boolean) => {
      if (online.inGame && game.paused !== on) online.togglePause();
    },
  };
  (window as unknown as { __spol: unknown }).__spol = {
    get info() {
      return { active: game.spol.active, rate: Math.round(game.rate * 100) / 100, allowed: game.spol.allowed, disabled: game.spol.disabled, tick: game.state.tick };
    },
    set: (on: boolean) => {
      game.spol.disabled = !on;
    },
  };
  if (import.meta.env.DEV && fixture) mountFixtureToggle(fixture);
  // UI-testens krog (scripts/uitest.mjs): bynavnenes skærmrektangler og saftens bobler.
  (window as unknown as { __ui: unknown }).__ui = {
    labels: () => sc.labelRects(),
    glide: (dx: number, dy: number, ms: number) => sc.debugGlide(dx, dy, ms),
    // Hvad rammer en finger, der lander her (sidens koordinater)? Til maptest's finale.
    hit: (x: number, y: number) => {
      const r = stage.getBoundingClientRect();
      const p = { x: x - r.left, y: y - r.top };
      return { node: sc.hitNode(p), recent: sc.hitNodeRecent(p), threat: sc.hitThreat(p), line: sc.hitLine(p) };
    },
    // Kortets målekrog (scripts/maptest.mjs): noder og navne i sidens koordinater, kameraets
    // stilling, og for hver vejrbrik om fronten har en hændelse, der stadig er en fare.
    map: () => {
      const r = stage.getBoundingClientRect();
      const off = (b: { x: number; y: number; w: number; h: number }) => ({ x: b.x + r.left, y: b.y + r.top, w: b.w, h: b.h });
      const pr = sc.mapProbe();
      const s = game.state;
      const fronts: Record<string, { live: number; shooed: number }> = {};
      for (const t of s.threats) {
        if (!t.front || t.phase === 'done') continue;
        const f = (fronts[t.front] ??= { live: 0, shooed: 0 });
        if (t.shooed) f.shooed++;
        else f.live++;
      }
      return {
        cam: pr.cam,
        fitScale: pr.fitScale,
        held: controls.holding,
        touching: sc.fingerDown,
        nodes: pr.nodes.map((n) => ({ ...n, name: nodeById(s, n.id)?.name ?? '', box: off(n.box), label: n.label ? off(n.label) : null })),
        objects: pr.objects.map((o) => ({ ...o, box: off(o.box) })),
        stuck: pr.stuck,
        gliding: pr.gliding,
        labels: pr.labels,
        fronts,
        phase: s.phase,
        year: s.year,
      };
    },
    // Tillidstestens krog (scripts/tillidtest.mjs): hvert hint, der kom frem på banen, med tick og år.
    hints: () => hintLog.slice(),
    deck: () => ({ cards: deckLog.cards.slice(), offers: deckLog.offers.map((o) => o.slice()), picked: deckLog.picked.slice(), refillable: [...REFILLABLE] }),
    // Mp-e2e's hjertetjek: rummets tillid og banens fulde antal hjerter, mod HUD'ets hjerter.
    trust: () => ({ trust: game.state.trust, max: trustMax(game.state) }),
    // Mp-e2e's hint-paritet: rådet, som hintet står på lige nu, og rummets 2,5 s-skel.
    advice: () => ({ tick: game.state.tick, bucket: Math.floor(game.state.tick / HOLD_TICKS), key: adviceKey, text: hintLast }),
    planned: () => controls.plannedList,
    node: (id: string) => {
      const n = nodeById(game.state, id);
      return n ? { station: n.station, free: slotsFree(game.state, n) } : null;
    },
    threat: (id: string) => {
      const t = game.state.threats.find((x) => x.id === id);
      return t ? { kind: t.kind, phase: t.phase, shooed: !!t.shooed } : null;
    },
    pop: (kind: 'puh' | 'lyn', id: string) => juice.pop(() => sc.nodeScreen(id), kind === 'puh' ? 'PUH!' : 'LYN! +3', kind, kind === 'puh' ? 1100 : 900),
  };
  install({
    state: () => ({
      ...summary(game.state),
      fps: Math.round(fps),
      autoplay,
      screen: screens.current,
      paused: game.paused,
      held: game.held,
      running: game.running,
      speed: game.speed,
      quality: quality.name,
      renderer: app.renderer.name,
      player: game.player,
      roles: game.rolesLive,
      hz: game.state.hz ? { dev: game.state.hz.dev, trims: game.state.hz.trims, earned: game.state.hz.earned, paid: game.state.hz.paid } : null,
      pilot: pilot ? { moves: pilot.moves, next: pilot.next?.label ?? null } : null,
    }),
    levels: () => LEVELS.map((l) => l.id),
    start: (level = levelId, d = diff, seed = params.seed) => {
      diff = d;
      newGame(getLevel(level).id, seed);
      play();
    },
    intent: (i) => {
      game.push(i.kind, i.target, i.opt, i.source ?? 'bot');
    },
    step: (seconds) => {
      game.stepSeconds(seconds);
      sc.frame(game.state, performance.now(), 16, 1);
      sc.render();
      return summary(game.state);
    },
    give: (kr) => {
      game.state.budget += kr;
    },
    event: (kind: ThreatKind) => {
      // Ikke-byggede farer (M3/M4) springes over med en advarsel, så en test ikke venter forgæves.
      if (!IMPLEMENTED.includes(kind)) {
        console.warn(`__game.event: "${kind}" er ikke bygget endnu (kun ${IMPLEMENTED.join(', ')}).`);
        return;
      }
      const s = game.state;
      const d = difficulty(s.difficulty);
      const warnTicks = d.warnS * TICK_HZ;
      const evs: SimEvent[] = [];
      startThreat(s, { id: `D${s.tick}`, kind, hitAt: s.clockTick + warnTicks, scripted: true, warnTicks, shifted: 0, cost: 0, finale: false }, d.stormLines, evs);
      onEvents(evs, s);
    },
    ghost: (on) => {
      const brain = on ? newbieBrain(true, game.player, on === 'lazy') : null;
      // 'both': tager begge gaver hvert år, så posen tømmes (tillidstestens årsskifte).
      game.ghost =
        brain && on === 'both'
          ? (st) => (st.phase === 'yearend' && st.packOffer.length >= 2 ? { player: game.player, clientSeq: 0, kind: 'pick_both', source: 'bot' } : brain(st))
          : brain;
    },
    autoplay: (model) => {
      autoplay = model;
      game.brain = model ? autoplayBrain(model, game.state.seed) : null;
    },
    nodeScreen: (id) => page(sc.nodeScreen(id)),
    lineScreen: (id) => page(sc.lineScreen(id)),
    threatScreen: (id) => page(sc.threatScreen(id)),
    // Vælg noden som et tryk gør: kortet skjules næste billede, hvis scenen ikke har den valgt.
    inspect: (id) => {
      controls.select(id);
      nodeCard.show(id);
    },
    // Runde 3-testens krog: fyld nodens pladser med kopier af en af dens egne ledninger, så en ny by
    // kan blive blokeret af en fuld nabo uden et langt spil op til det.
    fill: (id) => {
      const s = game.state;
      const n = nodeById(s, id);
      const base = s.lines.find((l) => !l.fixed && (l.a === id || l.b === id));
      if (!n || !base) return false;
      for (let i = 0; slotsFree(s, n) > 0 && i < 8; i++) s.lines.push({ ...base, id: `${base.id}~${i}` });
      markDirty(s);
      return true;
    },
    pause: (on) => {
      game.paused = on;
    },
    speed: (n) => {
      game.speed = n;
    },
    pick: (i) => {
      pickPack(i);
    },
  });
}

boot().catch((err: unknown) => {
  console.error(err);
  const app = document.getElementById('app');
  if (app) {
    app.classList.add('ready');
    app.append(
      h(
        'div',
        { class: 'boot-error', role: 'alert' },
        h('h1', { class: 'title small' }, 'NETTET VILLE IKKE STARTE'),
        h('p', {}, 'Din browser kunne ikke tegne kortet. Prøv at genindlæse siden eller at bruge en nyere browser.'),
      ),
    );
  }
});
